import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Container,
  Box,
  Stack,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Typography,
  Popover,
  Button,
  Tooltip,
  Paper,
  Pagination,
  Card,
  CardContent,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import {
  FileDownload,
  Refresh as RefreshIcon,
  CalendarMonth,
  EventAvailable,
  Block as BlockIcon,
  ArrowDropDown,
} from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import { format, addDays, isSameDay } from 'date-fns';
import { formatDateWithSeconds } from '../utils/dateUtils';
import { filterPersonNameOptions, formatPersonNameOptionLabel, sanitizeNameSearchInput } from '../utils/nameSearchInput';
import { employeeTypeFilterLabel, employeeTypeFilterToRoleParams, normalizeEmployeeTypeFilter } from '../utils/employeeTypeUi';
import { AttendanceGridLayout, getAttendanceEventImageCacheKeys, mergeAttendanceSnapshotCache } from './AuditAttendanceGridLayout';
import MissingEntrySearchModal from './MissingEntrySearchModal';
import AuditDialogs from './AuditDialogs';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import {
  pageFilterActionButtonSx,
  pageFilterAutocompleteSx,
  pageFilterDatePickerSx,
  pageFilterFormControlSx,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  createTodayDateRange,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  isDefaultDateTimeRange,
} from '../utils/filterDefaults';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useAuth } from '../contexts/AuthContext';
import { isKivoFeatureEnabled } from '../utils/kivoFeature';
import {
  getAttendanceSummaryCards,
  getAttendancePersonTimeline,
  getPersonFaces,
  getFaceImage,
  getAttendanceImageBlob,
  getAuditImageBlob,
  getLateEntryTime,
  exportAttendanceLogs,
  markAttendance,
  fetchHrmsEmployees,
  getCompanyPickerConfig,
  matchAuditImage,
  deleteAttendanceLog,
  revokeAttendanceByAudit,
  getAllPersonNames
} from '../utils/api';
import { peek as peekAuditImage } from '../services/auditImageCache';
import { useLocation, useSearchParams } from 'react-router-dom';
import { keyframes } from '@mui/system';
import * as XLSX from 'xlsx';

const NAME_SEARCH_DEBOUNCE_MS = 400;
const PRESENT_EMPLOYEE_TYPE_FILTER_DEFAULT = 'all';

const resolvePresentAuditImage = (auditId, attendanceSnapshots = {}) => {
  if (!auditId) return null;
  if (attendanceSnapshots[auditId]) return attendanceSnapshots[auditId];
  const sessionUrl = peekAuditImage(auditId);
  return sessionUrl !== undefined ? sessionUrl : null;
};

/** Normalize API flag so 0 / "false" from JSON still means off; default on when missing. */
function readLateEntryTrackingEnabled(res) {
  if (!res || typeof res !== 'object') return true;
  const v = res.late_entry_activate;
  if (v === false || v === 0 || v === '0') return false;
  if (typeof v === 'string' && v.toLowerCase() === 'false') return false;
  return true;
}

const AuditPresentContent = ({
  availableNames,
  aPersonName,
  setAPersonName,
  aAction,
  setAAction,
  aPremisesFilter,
  setAPremisesFilter,
  aEmployeeTypeFilter,
  setAEmployeeTypeFilter,
  aLateFilter,
  setALateFilter,
  aAttendanceSync,
  setAAttendanceSync,
  aSortOrder,
  setASortOrder,
  setADatePickerAnchor,
  aDateRange,
  aDatePickerOpen,
  aDatePickerAnchor,
  isMobile,
  primary,
  setADateRange,
  aStartTime,
  setAStartTime,
  aEndTime,
  setAEndTime,
  loadAttendanceSummary,
  aPagination,
  handleOpenAttendanceExportDialog,
  exportAttendanceLoading,
  aLoading,
  handleOpenMarkAttendance,
  AttendanceCardSkeleton,
  filteredAttendanceSummaries,
  personThumbnails,
  attendanceSnapshots,
  lateEntryTime,
  lateEntryTrackingEnabled = true,
  openTimingDialog,
  handleAttendancePageChange,
  onMissingEntrySearchClick,
  onLoadAttendanceTimeline,
  onRequestAttendanceEventImages,
  onRequestPersonThumbnail,
}) => {
  const { permissions } = useAuth();
  const kivoFeatureEnabled = isKivoFeatureEnabled(permissions);
  const [personNameInput, setPersonNameInput] = useState(aPersonName || '');
  const [lateFilterMenuOpen, setLateFilterMenuOpen] = useState(false);
  /** When late tracking is off, UI-only Late filter value (`all` = normal view, `late` = show disabled message). API stays `late_filter=false`. */
  const [lateFilterUiWhenDisabled, setLateFilterUiWhenDisabled] = useState('all');
  const [attendanceExportMenuAnchor, setAttendanceExportMenuAnchor] = useState(null);
  const attendanceExportMenuOpen = Boolean(attendanceExportMenuAnchor);
  // Draft date/time while the picker is open; committed filters (URL + cards) update only on Apply.
  const [draftDateRange, setDraftDateRange] = useState(aDateRange);
  const [draftStartTime, setDraftStartTime] = useState(aStartTime);
  const [draftEndTime, setDraftEndTime] = useState(aEndTime);

  useEffect(() => {
    setLateFilterMenuOpen(false);
    setLateFilterUiWhenDisabled('all');
  }, [lateEntryTrackingEnabled]);

  useEffect(() => {
    if (!aDatePickerOpen) return;
    const selection = aDateRange?.[0] || {};
    setDraftDateRange([{
      startDate: new Date(selection.startDate || new Date()),
      endDate: new Date(selection.endDate || selection.startDate || new Date()),
      key: 'selection',
    }]);
    setDraftStartTime(aStartTime);
    setDraftEndTime(aEndTime);
  }, [aDatePickerOpen, aDateRange, aStartTime, aEndTime]);

  const closeDatePickerWithoutApplying = useCallback(() => {
    setADatePickerAnchor(null);
  }, [setADatePickerAnchor]);

  const applyDatePickerFilters = useCallback(() => {
    const nextRange = [{
      startDate: new Date(draftDateRange?.[0]?.startDate || new Date()),
      endDate: new Date(draftDateRange?.[0]?.endDate || draftDateRange?.[0]?.startDate || new Date()),
      key: 'selection',
    }];
    const nextStartTime = draftStartTime || DEFAULT_START_TIME;
    const nextEndTime = draftEndTime || DEFAULT_END_TIME;
    setADateRange(nextRange);
    setAStartTime(nextStartTime);
    setAEndTime(nextEndTime);
    setADatePickerAnchor(null);
    loadAttendanceSummary(aPagination.limit, 0, {
      dateRange: nextRange,
      startTime: nextStartTime,
      endTime: nextEndTime,
    });
  }, [
    aPagination.limit,
    draftDateRange,
    draftEndTime,
    draftStartTime,
    loadAttendanceSummary,
    setADatePickerAnchor,
    setADateRange,
    setAEndTime,
    setAStartTime,
  ]);

  const applyPersonNameFilterNow = useCallback((rawValue) => {
    const sanitizedValue = sanitizeNameSearchInput(rawValue || '');
    setPersonNameInput(sanitizedValue);
    if (sanitizedValue !== aPersonName) {
      setAPersonName(sanitizedValue);
    }
  }, [aPersonName, setAPersonName]);

  const openAttendanceExportMenu = (event) => {
    setAttendanceExportMenuAnchor(event.currentTarget);
  };

  const closeAttendanceExportMenu = () => {
    setAttendanceExportMenuAnchor(null);
  };

  const handleChooseAttendanceExportFormat = (formatType) => {
    closeAttendanceExportMenu();
    if (typeof handleOpenAttendanceExportDialog === 'function') {
      handleOpenAttendanceExportDialog(formatType);
    }
  };

  useEffect(() => {
    setPersonNameInput(aPersonName || '');
  }, [aPersonName]);

  const hasActiveFilters = useMemo(() => (
    !!(aPersonName?.trim()) ||
    aEmployeeTypeFilter !== PRESENT_EMPLOYEE_TYPE_FILTER_DEFAULT ||
    !!aAction ||
    !!aPremisesFilter ||
    aLateFilter ||
    (kivoFeatureEnabled && aAttendanceSync && aAttendanceSync !== 'all') ||
    aSortOrder !== 'desc' ||
    !isDefaultDateTimeRange(aDateRange, aStartTime, aEndTime) ||
    (lateEntryTrackingEnabled === false && lateFilterUiWhenDisabled === 'late')
  ), [
    aAction,
    aAttendanceSync,
    aDateRange,
    aEmployeeTypeFilter,
    aEndTime,
    aLateFilter,
    aPersonName,
    aPremisesFilter,
    aSortOrder,
    aStartTime,
    kivoFeatureEnabled,
    lateEntryTrackingEnabled,
    lateFilterUiWhenDisabled,
  ]);

  const handleClearAllFilters = useCallback(() => {
    const todayRange = createTodayDateRange();
    setPersonNameInput('');
    setAPersonName('');
    setAEmployeeTypeFilter(PRESENT_EMPLOYEE_TYPE_FILTER_DEFAULT);
    setAAction('');
    setAPremisesFilter('');
    setALateFilter(false);
    setLateFilterUiWhenDisabled('all');
    setAAttendanceSync('all');
    setASortOrder('desc');
    setADateRange(todayRange);
    setAStartTime(DEFAULT_START_TIME);
    setAEndTime(DEFAULT_END_TIME);
    setADatePickerAnchor(null);
    loadAttendanceSummary(aPagination.limit, 0, {
      dateRange: todayRange,
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
      personName: '',
      action: '',
      premisesFilter: '',
      employeeTypeFilter: PRESENT_EMPLOYEE_TYPE_FILTER_DEFAULT,
      lateFilter: false,
      attendanceSync: 'all',
      sortOrder: 'desc',
    });
  }, [
    aPagination.limit,
    loadAttendanceSummary,
    setAAction,
    setAAttendanceSync,
    setADateRange,
    setAEmployeeTypeFilter,
    setAEndTime,
    setALateFilter,
    setAPersonName,
    setAPremisesFilter,
    setASortOrder,
    setAStartTime,
    setADatePickerAnchor,
  ]);

  const renderAttendanceContent = () => {
    const lateEntriesDisabledPaper = (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Late entries are disabled
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Late entry tracking is turned off. Turn it on under{' '}
          <strong>Settings</strong> → <strong>Late Entry configuration</strong> to use the late filter and late
          badges.
        </Typography>
      </Paper>
    );

    if (!filteredAttendanceSummaries || filteredAttendanceSummaries.length === 0) {
      if (lateEntryTrackingEnabled === false && lateFilterUiWhenDisabled === 'late') {
        return lateEntriesDisabledPaper;
      }
      return (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>No attendance behaviour found</Typography>
          <Typography variant="body2" color="text.secondary">
            {aPremisesFilter
              ? `No employees currently ${aPremisesFilter === 'in' ? 'in premises' : 'out of premises'}.`
              : 'Adjust filters or widen the day range to see cards.'}
          </Typography>
        </Paper>
      );
    }

    if (lateEntryTrackingEnabled === false && lateFilterUiWhenDisabled === 'late') {
      /* Late filter is UI-only when off; do not show attendance cards — message only */
      return lateEntriesDisabledPaper;
    }

    return (
      <AttendanceGridLayout
        summaries={filteredAttendanceSummaries}
        personThumbnails={personThumbnails}
        snapshotCache={attendanceSnapshots}
        lateEntryTime={lateEntryTime}
        lateEntryTrackingEnabled={lateEntryTrackingEnabled}
        lazyLoadImages
        onAttendanceEventLogClick={({ log, image }) => {
          const capturedTs = getCapturedTimestamp(log);
          const requestSentTs = getRequestSentTimestamp(log);
          const apiSubmitTs = getApiSubmitTimestamp(log);
          const apiReceivedTs = getApiReceivedTimestamp(log);
          const logWithStack = {
            ...log,
            capturedTs,
            requestSentTs,
            apiSubmitTs,
            apiReceivedTs,
          };
          openTimingDialog({
            log: logWithStack,
            stack: [{ log: logWithStack, image }],
          });
        }}
        onMissingEntrySearchClick={onMissingEntrySearchClick}
        onLoadAttendanceTimeline={onLoadAttendanceTimeline}
        onRequestAttendanceEventImages={onRequestAttendanceEventImages}
        onRequestPersonThumbnail={onRequestPersonThumbnail}
      />
    );
  };

  const formatEnrolledPersonNameOption = useCallback((option) => {
    return formatPersonNameOptionLabel(option, { includeCode: true });
  }, []);
  const filterEnrolledPersonNameOptions = useCallback((options, state) => {
    return filterPersonNameOptions(options, state);
  }, []);


  return (
        <Box>
      {/* Filters Section */}
          <Box sx={pageFiltersToolbarSx}>
        <Stack {...pageFiltersStackSx}>
          <Box sx={pageFiltersControlsSx}>
            <Autocomplete
              size="small"
              freeSolo
              options={availableNames}
              getOptionLabel={formatEnrolledPersonNameOption}
              filterOptions={filterEnrolledPersonNameOptions}
              value={personNameInput || null}
              inputValue={personNameInput || ''}
              onInputChange={(e, newValue) => setPersonNameInput(sanitizeNameSearchInput(newValue || ''))}
              onChange={(e, newValue) => {
                    const selectedValue =
                      typeof newValue === 'string'
                        ? newValue
                        : newValue?.name || '';
                    applyPersonNameFilterNow(selectedValue);
                  }} 
                renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by Name or person id"
                  InputLabelProps={{ shrink: true }}
                  onKeyDown={(e) => {
                    if (typeof params?.inputProps?.onKeyDown === 'function') {
                      params.inputProps.onKeyDown(e);
                    }
                    if (e.key !== 'Enter') return;
                    applyPersonNameFilterNow(e.currentTarget.value || '');
                  }}
                />
              )}
              sx={pageFilterAutocompleteSx(200)}
            />

              <FormControl
                size="small"
                sx={pageFilterFormControlSx(170)}
              >
                <InputLabel id="present-employee-type-label" shrink>
                  Attendance Type
                </InputLabel>
                <Select
                  labelId="present-employee-type-label"
                  value={aEmployeeTypeFilter}
                  label="Attendance Type"
                  onChange={(e) => setAEmployeeTypeFilter(e.target.value)}
                  displayEmpty
                  renderValue={employeeTypeFilterLabel}
                >
                  <MenuItem value="employee">Employee</MenuItem>
                  <MenuItem value="vendor">Vendor</MenuItem>
                  <MenuItem value="visitor">Visitor</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={pageFilterFormControlSx(150)}
              >
                <InputLabel id="action-label" shrink>
                  Select Action
                </InputLabel>

                <Select
                  labelId="action-label"
                  value={aAction}
                  label="Select Action"
                  onChange={(e) => setAAction(e.target.value)}
                  displayEmpty
                  renderValue={(v) => v ? (v === 'check_in' ? 'Check-In' : v === 'check_out' ? 'Check-Out' : 'All') : 'All'}
                >
                  <MenuItem value="">
                    All
                  </MenuItem>
                  <MenuItem value="check_in">Check-In</MenuItem>
                  <MenuItem value="check_out">Check-Out</MenuItem>
                </Select>
              </FormControl>

            
              <FormControl
                size="small"
                sx={pageFilterFormControlSx(170)}
              >
                <InputLabel id="premises-label" shrink>
                  Premises Status
                </InputLabel>

                <Select
                  labelId="premises-label"
                  value={aPremisesFilter}
                  label="Premises Status"
                  onChange={(e) => setAPremisesFilter(e.target.value)}
                  displayEmpty
                  renderValue={(v) => v ? (v === 'in' ? 'In Premises' : 'Out of Premises') : 'All Status'}
                >
                  <MenuItem value="">
                    All Status
                  </MenuItem>
                  <MenuItem value="in">In Premises</MenuItem>
                  <MenuItem value="out">Out of Premises</MenuItem>
                </Select>
              </FormControl>


              <FormControl
                size="small"
                sx={pageFilterFormControlSx(160)}
              >
                <InputLabel id="late-filter-label" shrink sx={{ pointerEvents: 'none' }}>
                  Late Filter
                </InputLabel>

                <Select
                  key={lateEntryTrackingEnabled ? 'late-on' : 'late-off'}
                  labelId="late-filter-label"
                  value={
                    lateEntryTrackingEnabled
                      ? (aLateFilter ? 'late' : 'all')
                      : lateFilterUiWhenDisabled
                  }
                  label="Late Filter"
                  open={lateFilterMenuOpen}
                  onOpen={() => setLateFilterMenuOpen(true)}
                  onClose={() => setLateFilterMenuOpen(false)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (lateEntryTrackingEnabled === false) {
                      setLateFilterUiWhenDisabled(v === 'late' ? 'late' : 'all');
                      setALateFilter(false);
                      return;
                    }
                    setALateFilter(v === 'late');
                  }}
                  displayEmpty
                  renderValue={(v) =>
                    v === 'late' ? 'Late Entries' : 'All Entries'
                  }
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: (theme) => ({
                        zIndex: theme.zIndex.modal + 1,
                      }),
                    },
                  }}
                >
                  <MenuItem value="all">All Entries</MenuItem>
                  <MenuItem value="late">Late Entries</MenuItem>
                </Select>
              </FormControl>

              {kivoFeatureEnabled && (
              <FormControl
                size="small"
                sx={pageFilterFormControlSx(170)}
              >
                <InputLabel id="attendance-sync-label" shrink>
                  Attendance Sync
                </InputLabel>
                <Select
                  labelId="attendance-sync-label"
                  value={aAttendanceSync || 'all'}
                  label="Attendance Sync"
                  onChange={(e) => setAAttendanceSync(e.target.value)}
                  displayEmpty
                  renderValue={(v) =>
                    v === 'synced' ? 'Synced' : v === 'unsynced' ? 'Unsynced' : 'All'
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="synced">Synced</MenuItem>
                  <MenuItem value="unsynced">Unsynced</MenuItem>
                </Select>
              </FormControl>
              )}

              <FormControl
                size="small"
                sx={pageFilterFormControlSx(160)}
              >
                <InputLabel id="attendance-sort-order-label" shrink>
                  Sort By 
                </InputLabel>

                <Select
                  labelId="attendance-sort-order-label"
                  value={aSortOrder}
                  label="Sort By"
                  onChange={(e) => setASortOrder(e.target.value)}
                  displayEmpty
                  renderValue={(v) => (v === 'asc' ? 'Time : Earliest to Latest' : ' Time : Latest to Earliest')}
                >
                  <MenuItem value="desc">Time : Latest to Earliest</MenuItem>
                  <MenuItem value="asc">Time : Earliest to Latest</MenuItem>
                </Select>
              </FormControl>

            <Box
              onClick={(e) => setADatePickerAnchor(e.currentTarget)}
              sx={pageFilterDatePickerSx(180)}
            >
              <CalendarMonth fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                {isSameDay(aDateRange[0].startDate, aDateRange[0].endDate)
                  ? format(aDateRange[0].startDate, 'MMM dd, yyyy')
                  : `${format(aDateRange[0].startDate, 'MMM dd')} - ${format(aDateRange[0].endDate, 'MMM dd, yyyy')}`}
              </Typography>
            </Box>
            <Popover
              open={aDatePickerOpen}
              anchorEl={aDatePickerAnchor}
              onClose={closeDatePickerWithoutApplying}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', overflow: 'auto' }
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Quick Select Presets */}
                <Box sx={{
                  p: 2,
                  borderRight: { md: '1px solid' },
                  borderBottom: { xs: '1px solid', md: 'none' },
                  borderColor: 'divider',
                  minWidth: 140,
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Quick Select
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                      { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                      { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                      { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                      { label: 'This Month', getValue: () => {
                        const now = new Date();
                        return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                      }},
                      { label: 'Last Month', getValue: () => {
                        const now = new Date();
                        return { startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1), endDate: new Date(now.getFullYear(), now.getMonth(), 0) };
                      }},
                    ].map((preset) => (
                      <Button
                        key={preset.label}
                        size="small"
                        variant="text"
                        onClick={() => {
                          const { startDate, endDate } = preset.getValue();
                          setDraftDateRange([{ startDate, endDate, key: 'selection' }]);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.75,
                          color: 'text.primary',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
              </Stack>
                </Box>

                {/* Date Range Picker - Responsive */}
                <Box sx={{
                  '& .rdrCalendarWrapper': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  },
                  '& .rdrMonth': {
                    width: { xs: '280px', sm: '300px' },
                  },
                }}>
                  <DateRangePicker
                    ranges={draftDateRange}
                    onChange={(ranges) => setDraftDateRange([ranges.selection])}
                    moveRangeOnFirstSelection={false}
                    months={isMobile ? 1 : 2}
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    rangeColors={[primary]}
                    staticRanges={[]}
                    inputRanges={[]}
                    maxDate={new Date()}
                  />
                  <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      size="small"
                      value={draftStartTime}
                      onChange={(e) => setDraftStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      size="small"
                      value={draftEndTime}
                      onChange={(e) => setDraftEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                    <Button
                      onClick={() => {
                        const today = new Date();
                        setDraftDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                        setDraftStartTime(DEFAULT_START_TIME);
                        setDraftEndTime(DEFAULT_END_TIME);
                      }}
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                    >
                      Reset
                    </Button>
                    <Button onClick={closeDatePickerWithoutApplying} variant="outlined" size="small">
                      Cancel
                    </Button>
                    <Button
                      onClick={applyDatePickerFilters}
                      variant="contained"
                      size="small"
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Popover>

            <ClearAllFiltersButton
              onClear={handleClearAllFilters}
              disabled={!hasActiveFilters}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          </Box>

          <Box sx={pageFiltersActionsSx}>
            {/* Export Button */}
            <Tooltip title="Export">
              <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownload />}
                  endIcon={<ArrowDropDown />}
                  onClick={openAttendanceExportMenu}
                  disabled={exportAttendanceLoading || aLoading}
                  sx={{
                    ...pageFilterActionButtonSx,
                    width: { xs: '100%', lg: 'auto' },
                  }}
                >
                  Export
                </Button>
                <Menu
                  anchorEl={attendanceExportMenuAnchor}
                  open={attendanceExportMenuOpen}
                  onClose={closeAttendanceExportMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={() => handleChooseAttendanceExportFormat('csv')}>Export in CSV</MenuItem>
                  <MenuItem onClick={() => handleChooseAttendanceExportFormat('xlsx')}>Export in XLSX</MenuItem>
                </Menu>
              </Box>
            </Tooltip>
            
            {/* Mark Attendance Button */}
            <Button
              variant="contained"
              startIcon={<EventAvailable />}
              onClick={handleOpenMarkAttendance}
              sx={{
                ...pageFilterActionButtonSx,
                width: { xs: '100%', lg: 'auto' },
              }}
            >
              Mark Attendance
            </Button>
          </Box>
        </Stack>
      </Box>
      <Box sx={{ minHeight: 200, position: 'relative' }}>
        <Box
          sx={{
            position: aLoading ? 'relative' : 'absolute',
            inset: 0,
            opacity: aLoading ? 1 : 0,
            transform: aLoading ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 260ms ease, transform 260ms ease',
            pointerEvents: aLoading ? 'auto' : 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            {Array.from({ length: 3 }).map((_, idx) => (
              <Box key={`attendance-skel-${idx}`} sx={{ flex: 1, minWidth: 0 }}>
                <AttendanceCardSkeleton />
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            position: aLoading ? 'absolute' : 'relative',
            inset: 0,
            opacity: aLoading ? 0 : 1,
            transform: aLoading ? 'translateY(8px)' : 'translateY(0)',
            transition: 'opacity 300ms ease, transform 300ms ease',
            pointerEvents: aLoading ? 'none' : 'auto',
          }}
        >
          {renderAttendanceContent()}
        </Box>
      </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
            Showing {aPagination.total === 0 ? 0 : aPagination.offset + 1}-{Math.min(aPagination.offset + aPagination.limit, aPagination.total)} of {aPagination.total} people
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">Cards per page:</Typography>
                <Select
                  size="small"
                  value={aPagination.limit}
              onChange={(e) => loadAttendanceSummary(e.target.value, 0)}
              sx={{ minWidth: 90 }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
              <MenuItem value={75}>75</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Stack>
            </Stack>
            <Pagination
              count={Math.max(1, Math.ceil((aPagination.total || 0) / aPagination.limit))}
              page={aPagination.current_page}
              onChange={handleAttendancePageChange}
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
  );
};

const PRIMARY = '#40518A';

// Anonymous image for manual attendance and loading states
const ANONYMOUS_IMAGE = '/anonymous.webp';

const getResultMeta = (log) => {
  if (!log) return { label: 'Unknown', color: 'default' };
  if (log.success) return { label: 'Matched', color: 'success' };
  if (log.confidence_level === 'possible_match') return { label: 'Possible Match', color: 'warning' };
  if (log.confidence_level === 'no_match') return { label: 'Unmatched', color: 'error' };
  return { label: 'Failed', color: 'error' };
};

const isAttendanceSent = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

const getLogRequestMetadata = (log) => {
  if (!log) return {};
  const raw = log.request_metadata;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

const getAttendanceRefFromLog = (log) => {
  const metadata = getLogRequestMetadata(log);
  const topLevelManualAttendance = log?.manual_attendance && typeof log.manual_attendance === 'object'
    ? log.manual_attendance
    : null;
  const manualAttendance = metadata.manual_attendance && typeof metadata.manual_attendance === 'object'
    ? metadata.manual_attendance
    : null;
  const attendanceMeta = metadata.attendance && typeof metadata.attendance === 'object'
    ? metadata.attendance
    : null;
  const attendanceRevocation = metadata.attendance_revocation && typeof metadata.attendance_revocation === 'object'
    ? metadata.attendance_revocation
    : null;

  const attendanceId =
    (topLevelManualAttendance && topLevelManualAttendance.attendance_id) ||
    (manualAttendance && manualAttendance.attendance_id) ||
    (attendanceMeta && attendanceMeta.attendance_id) ||
    log?.attendance_id ||
    metadata.attendance_id ||
    null;

  const revoked = Boolean(
    log?.revoked === true ||
    (attendanceMeta && attendanceMeta.revoked) ||
    (attendanceRevocation && attendanceRevocation.revoked) ||
    metadata.attendance_revoked
  );

  return {
    attendanceId: attendanceId ? String(attendanceId) : null,
    revoked,
  };
};

const resolveBucketAttendanceRef = (representativeLog, bucketLogs = []) => {
  const entries = (Array.isArray(bucketLogs) ? bucketLogs : []).filter(Boolean);
  if (representativeLog) entries.unshift(representativeLog);

  const hasAttendance = entries.some((item) => isAttendanceSent(item?.send_attendance));
  const revoked = entries.some((item) => getAttendanceRefFromLog(item).revoked);
  const prioritized = [...entries].sort((a, b) => Number(isAttendanceSent(b?.send_attendance)) - Number(isAttendanceSent(a?.send_attendance)));
  const linked = prioritized
    .map((item) => ({ item, ref: getAttendanceRefFromLog(item) }))
    .find(({ ref }) => Boolean(ref.attendanceId));

  return {
    attendanceId: linked?.ref?.attendanceId || null,
    sourceAuditId: linked?.item?.audit_id || representativeLog?.audit_id || null,
    hasAttendance,
    revoked,
  };
};

const markLogAttendanceRevoked = (entry, attendanceId, revokedByName = null) => {
  if (!entry || typeof entry !== 'object') return entry;
  const metadata = { ...getLogRequestMetadata(entry) };
  const attendanceMeta = metadata.attendance && typeof metadata.attendance === 'object'
    ? { ...metadata.attendance }
    : {};
  const revocationTs = new Date().toISOString();
  attendanceMeta.attendance_id = attendanceId;
  attendanceMeta.revoked = true;
  attendanceMeta.revoked_at = revocationTs;
  attendanceMeta.revoked_by_name = revokedByName || attendanceMeta.revoked_by_name || null;
  metadata.attendance = attendanceMeta;
  metadata.attendance_revocation = {
    revoked: true,
    attendance_id: attendanceId,
    revoked_at: revocationTs,
    revoked_by_name: revokedByName || null,
  };
  return {
    ...entry,
    send_attendance: false,
    revoked: true,
    request_metadata: metadata,
  };
};

const normaliseMs = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return Math.trunc(num);
};

const msToIsoString = (value) => {
  const num = normaliseMs(value);
  if (num === null) return '';
  const date = new Date(num);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const parseIsoToEpochMs = (value) => {
  if (!value && value !== 0) return Number.NaN;
  const text = String(value).trim();
  if (!text) return Number.NaN;
  const hasTimezone = /([+-]\d{2}:?\d{2}|Z)$/i.test(text);
  const normalizedText = hasTimezone ? text : `${text}Z`;
  const parsed = Date.parse(normalizedText);
  return Number.isNaN(parsed) ? Number.NaN : parsed;
};

const resolveTimestamp = (isoValue, msValue, fallbackIso) => {
  let resolvedIso = isoValue || '';
  let resolvedMs = normaliseMs(msValue);
  if (!resolvedIso) {
    const fromMs = msToIsoString(resolvedMs);
    if (fromMs) resolvedIso = fromMs;
  }
  if (!resolvedIso && fallbackIso) {
    resolvedIso = fallbackIso;
  }
  if ((resolvedMs === null || resolvedMs === undefined) && resolvedIso) {
    const parsed = parseIsoToEpochMs(resolvedIso);
    if (!Number.isNaN(parsed)) {
      resolvedMs = parsed;
    }
  }
  if (resolvedMs !== null && resolvedMs !== undefined) {
    const canonicalIso = msToIsoString(resolvedMs);
    if (canonicalIso) resolvedIso = canonicalIso;
  }
  return { iso: resolvedIso, ms: resolvedMs };
};

const getCapturedTimestamp = (log) => {
  const meta = log?.request_metadata || {};
  return resolveTimestamp(
    log?.captured_at || meta.captured_at,
    log?.captured_at_ms ?? meta.captured_at_ms,
    log?.created_at
  );
};

const getRequestSentTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log?.recognize_started_at ?? meta.recognize_started_at,
    log?.recognize_started_at_ms ?? meta.recognize_started_at_ms ?? null, null
  );
}

const getApiSubmitTimestamp = (log) => {
  const meta = log?.request_metadata || {};
  return resolveTimestamp(
    log?.api_submit_at || meta.api_submit_at,
    log?.api_submit_at_ms ?? meta.api_submit_at_ms,
    log?.created_at
  );
};

const getApiReceivedTimestamp = (log) => {
  const meta = log.request_metadata || {};
  return resolveTimestamp(
    log.api_received_at ?? meta.api_received_at ?? null,
    log?.api_received_at_ms ?? meta.api_received_at_ms ?? null,
    null
  );
}

const csvEscapeValue = (value) => {
  const stringValue = String(value ?? '');
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const formatExportValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map((item) => formatExportValue(item)).join(' | ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value).trim();
};

const convertJsonToCsv = (data, fieldsOverride = null, headersOverride = null) => {
  const fields = fieldsOverride || data?.fields;
  const headers = headersOverride || data?.headers;
  if (!data || !Array.isArray(fields) || !Array.isArray(headers) || fields.length !== headers.length) {
    throw new Error('Invalid export payload');
  }

  const rows = [];
  rows.push(headers.map((header) => csvEscapeValue(header)).join(','));
  for (const log of data.logs || []) {
    const values = fields.map((field) => csvEscapeValue(formatExportValue(log?.[field])));
    rows.push(values.join(','));
  }
  return rows.join('\n');
};

const downloadAttendanceXlsxFile = (
  exportPayload,
  fields = [],
  headers = [],
  fileName = 'attendance_logs',
) => {
  if (!exportPayload || !Array.isArray(fields) || !Array.isArray(headers) || fields.length !== headers.length || !fields.length) {
    throw new Error('Invalid XLSX export payload');
  }
  const logs = Array.isArray(exportPayload?.logs) ? exportPayload.logs : [];
  const worksheetRows = [
    headers,
    ...logs.map((log) => fields.map((field) => formatExportValue(log?.[field]))),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Logs');
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  const safeFileName = (fileName || 'attendance_logs').trim() || 'attendance_logs';
  XLSX.writeFile(workbook, `${safeFileName}_${timestamp}.xlsx`);
};

const normalizeEmployeeOption = (emp) => {
  const nested = (emp && typeof emp === 'object' && typeof emp.employee === 'object') ? emp.employee : {};
  const rawId = emp?.employee_id || emp?.id || nested?.employee_id || nested?.id || emp?.person_id || nested?.person_id;
  const person_id = rawId ? String(rawId) : '';
  const firstName = emp?.first_name || nested?.first_name || '';
  const lastName = emp?.last_name || nested?.last_name || '';
  const joinedName = `${firstName} ${lastName}`.trim();
  const name = emp?.full_name || nested?.full_name || emp?.name || nested?.name || joinedName || (emp?.employee_code ? `Employee ${emp.employee_code}` : '');
  const employee_code = emp?.employee_code || nested?.employee_code || '';
  const company_id = emp?.company_id || nested?.company_id || '';
  const branch_id = emp?.branch_id || nested?.branch_id || '';
  return person_id
    ? {
        person_id,
        name: name || `Employee ${person_id}`,
        employee_code,
        company_id,
        branch_id,
      }
    : null;
};

const mapAttendanceFilterToState = (attendanceFilter) => {
  if (attendanceFilter === 'total_in') {
    return { action: 'check_in', premisesFilter: '', lateFilter: false };
  }
  if (attendanceFilter === 'total_out') {
    return { action: 'check_out', premisesFilter: '', lateFilter: false };
  }
  if (attendanceFilter === 'in_premises') {
    return { action: '', premisesFilter: 'in', lateFilter: false };
  }
  if (attendanceFilter === 'out_premises') {
    return { action: '', premisesFilter: 'out', lateFilter: false };
  }
  if (attendanceFilter === 'late_entries') {
    return { action: '', premisesFilter: '', lateFilter: true };
  }
  return { action: '', premisesFilter: '', lateFilter: false };
};

// Shimmer animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Shimmer effect styles
const shimmerStyles = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
};

const LocalAttendanceCardSkeleton = () => (
  <Card 
    sx={{ 
      borderRadius: 3, 
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      {/* Header with avatar and name */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            ...shimmerStyles,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ width: '60%', height: 20, borderRadius: 1, mb: 0.5, ...shimmerStyles }} />
          <Box sx={{ width: '40%', height: 14, borderRadius: 1, ...shimmerStyles }} />
        </Box>
      </Stack>

      {/* Status chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ width: 70, height: 24, borderRadius: 12, ...shimmerStyles }} />
        <Box sx={{ width: 80, height: 24, borderRadius: 12, ...shimmerStyles }} />
        <Box sx={{ width: 90, height: 24, borderRadius: 12, ...shimmerStyles }} />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Recent Activity header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 100, height: 12, borderRadius: 1, ...shimmerStyles }} />
        <Stack direction="row" spacing={0.5}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, ...shimmerStyles }} />
          <Box sx={{ width: 40, height: 28, borderRadius: 1, ...shimmerStyles }} />
          <Box sx={{ width: 28, height: 28, borderRadius: 1, ...shimmerStyles }} />
        </Stack>
      </Stack>

      {/* Activity tiles */}
      <Stack direction="row" spacing={1.2}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ flex: 1 }}>
            <Box sx={{ width: '100%', height: 100, borderRadius: 2, mb: 1, ...shimmerStyles }} />
            <Box sx={{ width: '70%', height: 14, borderRadius: 1, mb: 0.5, ...shimmerStyles }} />
            <Box sx={{ width: '50%', height: 12, borderRadius: 1, ...shimmerStyles }} />
          </Box>
        ))}
      </Stack>
    </CardContent>
  </Card>
);

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const isValidCachedSnapshot = (value) => typeof value === 'string' && value.length > 0;

const AuditPresentStandalone = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm } = useConfirmDialog();
  const { permissions } = useAuth();
  const kivoFeatureEnabled = isKivoFeatureEnabled(permissions);
  const initialAttendanceFilter = location.state?.attendanceFilter;
  const initialAttendanceState = mapAttendanceFilterToState(initialAttendanceFilter);

  const [availableNames, setAvailableNames] = useState([]);
  const safeAvailableNames = Array.isArray(availableNames) ? availableNames : [];
  const [aPersonName, setAPersonName] = useState(searchParams.get('person_name') || '');
  const [aAction, setAAction] = useState(searchParams.get('action') || initialAttendanceState.action);
  const [aPremisesFilter, setAPremisesFilter] = useState(searchParams.get('premises_filter') || initialAttendanceState.premisesFilter);
  const [aEmployeeTypeFilter, setAEmployeeTypeFilter] = useState(
    () => normalizeEmployeeTypeFilter(searchParams.get('employee_type') || PRESENT_EMPLOYEE_TYPE_FILTER_DEFAULT),
  );
  const [aLateFilter, setALateFilter] = useState(searchParams.get('late_filter') === 'true' || initialAttendanceState.lateFilter);
  const [aAttendanceSync, setAAttendanceSync] = useState(() => {
    const v = (searchParams.get('attendance_sync') || 'all').toLowerCase();
    return v === 'synced' || v === 'unsynced' ? v : 'all';
  });
  const [aSortOrder, setASortOrder] = useState(searchParams.get('sort_order') || 'desc');
  const [aSummaries, setASummaries] = useState([]);
  const [aPagination, setAPagination] = useState({ limit: 100, offset: 0, current_page: 1, total: 0 });
  const [aLoading, setALoading] = useState(false);
  const [personThumbnails, setPersonThumbnails] = useState({});
  const personThumbnailsRef = useRef({});
  const thumbnailRequestsRef = useRef(new Set());
  const [attendanceSnapshots, setAttendanceSnapshots] = useState({});
  const attendanceSnapshotsRef = useRef({});
  const attendanceSnapshotRequestsRef = useRef(new Set());
  const [attendanceTimelineByKey, setAttendanceTimelineByKey] = useState({});
  const attendanceTimelineByKeyRef = useRef({});
  const attendanceTimelineRequestsRef = useRef(new Map());
  const [lateEntryTime, setLateEntryTime] = useState(null);
  const [lateEntryTrackingEnabled, setLateEntryTrackingEnabled] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [aDateRange, setADateRange] = useState(() => {
    const urlStart = searchParams.get('start_date');
    const urlEnd = searchParams.get('end_date');
    const startDate = urlStart ? new Date(urlStart) : new Date();
    const endDate = urlEnd ? new Date(urlEnd) : new Date();
    return [{
      startDate: isNaN(startDate.getTime()) ? new Date() : startDate,
      endDate: isNaN(endDate.getTime()) ? new Date() : endDate,
      key: 'selection'
    }];
  });
  const [aDatePickerAnchor, setADatePickerAnchor] = useState(null);
  const aDatePickerOpen = Boolean(aDatePickerAnchor);
  const [aStartTime, setAStartTime] = useState(searchParams.get('start_time') || '00:00');
  const [aEndTime, setAEndTime] = useState(searchParams.get('end_time') || '23:59');
  const [timingDialog, setTimingDialog] = useState({ open: false, log: null, stack: [] });
  const [missingEntrySearchModalOpen, setMissingEntrySearchModalOpen] = useState(false);
  const [missingEntrySearchProps, setMissingEntrySearchProps] = useState({
    missingEvent: null,
    personThumbnail: null,
    recentEvents: [],
    personId: null,
  });
  const [exportAttendanceLoading, setExportAttendanceLoading] = useState(false);
  const [attendanceExportDialogOpen, setAttendanceExportDialogOpen] = useState(false);
  const [attendanceExportFields, setAttendanceExportFields] = useState([]);
  const [attendanceExportSelectedFields, setAttendanceExportSelectedFields] = useState([]);
  const [attendanceExportFileName, setAttendanceExportFileName] = useState('attendance_logs');
  const [attendanceExportDateRange, setAttendanceExportDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  const [attendanceExportStartTime, setAttendanceExportStartTime] = useState('00:00');
  const [attendanceExportEndTime, setAttendanceExportEndTime] = useState('23:59');
  const [attendanceExportDatePickerAnchor, setAttendanceExportDatePickerAnchor] = useState(null);
  const attendanceExportDatePickerOpen = Boolean(attendanceExportDatePickerAnchor);
  const [attendanceExportPersonName, setAttendanceExportPersonName] = useState('');
  const [attendanceExportAllEmployeesPresence, setAttendanceExportAllEmployeesPresence] = useState(false);
  const [attendanceExportAction, setAttendanceExportAction] = useState('');
  const [attendanceExportPremisesFilter, setAttendanceExportPremisesFilter] = useState('');
  const [attendanceExportLateFilter, setAttendanceExportLateFilter] = useState(false);
  const [attendanceExportFormat, setAttendanceExportFormat] = useState('csv');

  const [employees, setEmployees] = useState([]);
  const safeEmployees = Array.isArray(employees) ? employees : [];
  const sortedEmployees = useMemo(
    () => [...safeEmployees].sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [safeEmployees]
  );
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const employeesCacheRef = useRef({});
  const employeesRequestRef = useRef(null);
  const [markAttendanceDialog, setMarkAttendanceDialog] = useState(false);
  const [markAttendanceSubmitting, setMarkAttendanceSubmitting] = useState(false);
  const [markAttendancePersonId, setMarkAttendancePersonId] = useState('');
  const [markAttendancePersonName, setMarkAttendancePersonName] = useState('');
  const [markAttendanceCheckIn, setMarkAttendanceCheckIn] = useState('');
  const [markAttendanceCheckOut, setMarkAttendanceCheckOut] = useState('');
  const [markAttendanceRemark, setMarkAttendanceRemark] = useState('');
  const [markAttendanceCompanyId, setMarkAttendanceCompanyId] = useState('');
  const [markAttendanceContext, setMarkAttendanceContext] = useState(null);
  const [attendanceCalendarOpen, setAttendanceCalendarOpen] = useState(false);
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(new Date());
  const [calendarType, setCalendarType] = useState('checkin');

  const [timingDialogPossibleMatchSelectedId, setTimingDialogPossibleMatchSelectedId] = useState(null);
  const [timingDialogPossibleMatchMarkSubmitting] = useState(false);
  const [timingDialogPossibleMatchCandidateImages] = useState({});
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [retryMatchDialogOpen, setRetryMatchDialogOpen] = useState(false);
  const [retryMatchAudit, setRetryMatchAudit] = useState(null);
  const [retryMatchThreshold, setRetryMatchThreshold] = useState('0.6');
  const [retryMatchStackImages, setRetryMatchStackImages] = useState([]);
  const [retryMatchSelectedAuditId, setRetryMatchSelectedAuditId] = useState(null);
  const [retryMatchImageLoading, setRetryMatchImageLoading] = useState(false);
  const [retryMatchResultData, setRetryMatchResultData] = useState(null);
  const [retryMatchResultLoading, setRetryMatchResultLoading] = useState(false);
  const [retryMatchAttendanceSubmitting, setRetryMatchAttendanceSubmitting] = useState(false);
  const retryMatchThresholdRef = useRef(0.6);
  const retryMatchContentRef = useRef(null);
  const retryMatchResultRef = useRef(null);
  const [revokeConfirmDialog, setRevokeConfirmDialog] = useState({
    open: false,
    topLog: null,
    bucketLogs: [],
    userName: '',
  });
  const [revokingAuditIds, setRevokingAuditIds] = useState(new Set());

  const [possibleMatchModal] = useState({ open: false, log: null, image: null });
  const [possibleMatchSelectedId] = useState(null);
  const [possibleMatchMarkSubmitting] = useState(false);
  const [possibleMatchCandidateImages] = useState({});
  const [confirmMarkAttendanceOpen] = useState(false);
  const [confirmMarkAttendanceSource] = useState(null);
  const [confirmMarkAttendancePersonName] = useState('');
  const trainMenuAnchor = null;
  const [previewOpen] = useState(false);
  const [previewUrl] = useState('');
  const possibleMatchTrainActiveRef = useRef(false);

  const [addToEmployeeDialog, setAddToEmployeeDialog] = useState(false);
  const [createEmployeeDialog, setCreateEmployeeDialog] = useState(false);
  const [presentDialogOpen, setPresentDialogOpen] = useState(false);
  const [presentSubmitting] = useState(false);
  const [presentPersonId, setPresentPersonId] = useState('');
  const [newPersonId, setNewPersonId] = useState('');
  const [newName, setNewName] = useState('');
  const [operationLoading] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [employeeCompanyId, setEmployeeCompanyId] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);

  const attendanceNavFilterKeyRef = useRef('');
  const attendanceNavFilterAppliedRef = useRef(true);
  const attendanceSummaryRequestIdRef = useRef(0);

  const OTHER_COMPANY_OPTION_VALUE = '__other_company__';

  const show = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 600 : false;

  // Sync state changes to URL query parameters
  useEffect(() => {
    setSearchParams((prevParams) => {
      const params = new URLSearchParams(prevParams);

      if (aPersonName) params.set('person_name', aPersonName); else params.delete('person_name');
      if (aAction) params.set('action', aAction); else params.delete('action');
      if (aPremisesFilter) params.set('premises_filter', aPremisesFilter); else params.delete('premises_filter');
      if (aEmployeeTypeFilter && aEmployeeTypeFilter !== PRESENT_EMPLOYEE_TYPE_FILTER_DEFAULT) {
        params.set('employee_type', aEmployeeTypeFilter);
      } else {
        params.delete('employee_type');
      }
      if (aLateFilter) params.set('late_filter', 'true'); else params.delete('late_filter');
      if (kivoFeatureEnabled && aAttendanceSync && aAttendanceSync !== 'all') {
        params.set('attendance_sync', aAttendanceSync);
      } else {
        params.delete('attendance_sync');
      }
      if (aSortOrder && aSortOrder !== 'desc') params.set('sort_order', aSortOrder); else params.delete('sort_order');

      if (aDateRange[0]?.startDate) {
        params.set('start_date', format(aDateRange[0].startDate, 'yyyy-MM-dd'));
      }
      if (aDateRange[0]?.endDate) {
        params.set('end_date', format(aDateRange[0].endDate, 'yyyy-MM-dd'));
      }
      if (aStartTime && aStartTime !== '00:00') params.set('start_time', aStartTime); else params.delete('start_time');
      if (aEndTime && aEndTime !== '23:59') params.set('end_time', aEndTime); else params.delete('end_time');

      return params.toString() !== prevParams.toString() ? params : prevParams;
    }, { replace: true });
  }, [aPersonName, aAction, aPremisesFilter, aEmployeeTypeFilter, aLateFilter, aAttendanceSync, aSortOrder, aDateRange, aStartTime, aEndTime, kivoFeatureEnabled, setSearchParams]);

  useEffect(() => {
    const loadNames = async () => {
      try {

        const res = await getAllPersonNames();
        const names = res?.map((d)=>`${d.name} [${d.person_id}]`) || [];

        names.sort((a, b) => a.localeCompare(b));
        setAvailableNames(res || []);
      } catch (e) {
        console.error('Failed to load unique names:', e);
      }
    };
    loadNames();
  }, []);

  useEffect(() => {
    const loadLateEntry = async () => {
      try {
        const res = await getLateEntryTime();
        if (res?.late_entry_time) setLateEntryTime(res.late_entry_time);
        setLateEntryTrackingEnabled(readLateEntryTrackingEnabled(res));
      } catch (e) {
        console.error('Failed to load late entry time:', e);
      }
    };
    loadLateEntry();
  }, []);

  useEffect(() => {
    if (!lateEntryTrackingEnabled) {
      setALateFilter(false);
      setAttendanceExportLateFilter(false);
    }
  }, [lateEntryTrackingEnabled]);

  useEffect(() => {
    personThumbnailsRef.current = personThumbnails;
  }, [personThumbnails]);

  useEffect(() => {
    attendanceSnapshotsRef.current = attendanceSnapshots;
  }, [attendanceSnapshots]);

  useEffect(() => {
    attendanceTimelineByKeyRef.current = attendanceTimelineByKey;
  }, [attendanceTimelineByKey]);

  const buildAttendanceDateTimeRange = useCallback((overrides = {}) => {
    const effectiveDateRange = Array.isArray(overrides.dateRange) && overrides.dateRange.length
      ? overrides.dateRange
      : aDateRange;
    const effectiveStartTime = typeof overrides.startTime === 'string' ? overrides.startTime : aStartTime;
    const effectiveEndTime = typeof overrides.endTime === 'string' ? overrides.endTime : aEndTime;
    const startDate = effectiveDateRange[0]?.startDate || new Date();
    const endDate = effectiveDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const [istStartHour, istStartMinute] = parseTime(effectiveStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(effectiveEndTime, 23, 59);
    const localStart = new Date(startDate);
    localStart.setHours(istStartHour, istStartMinute, 0, 0);
    const localEnd = new Date(endDate);
    localEnd.setHours(istEndHour, istEndMinute, 59, 0);
    if (localStart > localEnd) {
      return { error: 'Start date/time cannot be after end date/time' };
    }

    // IST is UTC+5:30 — convert user-entered IST wall-clock times to UTC for the API.
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0,
    ));
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);
    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      59,
    ));
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
    };
  }, [aDateRange, aStartTime, aEndTime]);

  const loadAttendanceSummary = useCallback(async (limit, offset, filterOverrides = {}) => {
    const requestId = ++attendanceSummaryRequestIdRef.current;
    try {
      setALoading(true);
      const range = buildAttendanceDateTimeRange(filterOverrides);
      if (range?.error) {
        if (requestId === attendanceSummaryRequestIdRef.current) {
          show(range.error, 'error');
        }
        return;
      }
      const { formattedStart, formattedEnd } = range;
      const effectivePersonName = filterOverrides.personName !== undefined
        ? filterOverrides.personName
        : aPersonName;
      const effectiveAction = filterOverrides.action !== undefined ? filterOverrides.action : aAction;
      const effectivePremisesFilter = filterOverrides.premisesFilter !== undefined
        ? filterOverrides.premisesFilter
        : aPremisesFilter;
      const effectiveEmployeeTypeFilter = filterOverrides.employeeTypeFilter !== undefined
        ? filterOverrides.employeeTypeFilter
        : aEmployeeTypeFilter;
      const effectiveLateFilter = filterOverrides.lateFilter !== undefined
        ? filterOverrides.lateFilter
        : aLateFilter;
      const effectiveAttendanceSync = filterOverrides.attendanceSync !== undefined
        ? filterOverrides.attendanceSync
        : aAttendanceSync;
      const effectiveSortOrder = filterOverrides.sortOrder !== undefined
        ? filterOverrides.sortOrder
        : aSortOrder;
      // Select Action = current premises (latest event in range). Premises dropdown wins if set.
      const premisesForApi =
        effectivePremisesFilter ||
        (effectiveAction === 'check_in' ? 'in' : effectiveAction === 'check_out' ? 'out' : '');
      const roleParams = employeeTypeFilterToRoleParams(effectiveEmployeeTypeFilter);
      const res = await getAttendanceSummaryCards({
        person_name: (effectivePersonName || '').trim(),
        action: '',
        start_time: formattedStart,
        end_time: formattedEnd,
        days: null,
        limit,
        offset,
        premises_filter: premisesForApi,
        late_filter: effectiveLateFilter,
        sort_order: effectiveSortOrder,
        attendance_sync: kivoFeatureEnabled ? (effectiveAttendanceSync || 'all') : 'all',
        is_intrusion: false,
        ...roleParams,
      });
      if (requestId !== attendanceSummaryRequestIdRef.current) return;
      const arr = Array.isArray(res?.summaries) ? res.summaries : [];
      const total = res?.pagination?.total || res?.total || 0;
      setASummaries(arr);
      setAPagination({ limit, offset, current_page: Math.floor(offset / limit) + 1, total });
    } catch (e) {
      if (requestId !== attendanceSummaryRequestIdRef.current) return;
      console.error(e);
      show('Failed to summarise attendance', 'error');
    } finally {
      if (requestId === attendanceSummaryRequestIdRef.current) {
        setALoading(false);
      }
    }
  }, [aPersonName, aAction, aPremisesFilter, aEmployeeTypeFilter, aLateFilter, aAttendanceSync, aSortOrder, buildAttendanceDateTimeRange, show, kivoFeatureEnabled]);

  const buildAttendanceTimelineCacheKey = useCallback((summary, formattedStart, formattedEnd) => {
    const personKey = summary?.person_id || summary?.person_name || summary?.employee_code || 'unknown';
    return `${personKey}|${formattedStart || ''}|${formattedEnd || ''}`;
  }, []);

  const loadAttendanceTimelineForSummary = useCallback(async (summary) => {
    if (!summary || (!summary.person_id && !summary.person_name)) return summary;
    const range = buildAttendanceDateTimeRange();
    if (range?.error) {
      show(range.error, 'error');
      return summary;
    }
    const { formattedStart, formattedEnd } = range;
    const cacheKey = buildAttendanceTimelineCacheKey(summary, formattedStart, formattedEnd);
    const cached = attendanceTimelineByKeyRef.current[cacheKey];
    if (cached) {
      return cached;
    }
    const inflight = attendanceTimelineRequestsRef.current.get(cacheKey);
    if (inflight) {
      return inflight;
    }
    const promise = (async () => {
      try {
        const response = await getAttendancePersonTimeline({
          person_id: summary.person_id || '',
          person_name: summary.person_name || '',
          start_time: formattedStart,
          end_time: formattedEnd,
          days: null,
          is_intrusion: false,
          ...employeeTypeFilterToRoleParams(aEmployeeTypeFilter),
        });
        const fullSummary = response?.summary || summary;
        setAttendanceTimelineByKey((prev) => ({ ...prev, [cacheKey]: fullSummary }));
        return fullSummary;
      } catch (error) {
        console.error('Failed to load attendance timeline:', error);
        show('Failed to load timeline details', 'error');
        return summary;
      } finally {
        attendanceTimelineRequestsRef.current.delete(cacheKey);
      }
    })();
    attendanceTimelineRequestsRef.current.set(cacheKey, promise);
    return promise;
  }, [aEmployeeTypeFilter, buildAttendanceDateTimeRange, buildAttendanceTimelineCacheKey, show]);

  useEffect(() => {
    loadAttendanceSummary(aPagination.limit, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aPersonName, aAction, aPremisesFilter, aEmployeeTypeFilter, aLateFilter, aAttendanceSync, aSortOrder]);

  useEffect(() => {
    setAttendanceTimelineByKey({});
    attendanceTimelineRequestsRef.current.clear();
  }, [aPersonName, aAction, aPremisesFilter, aEmployeeTypeFilter, aLateFilter, aAttendanceSync, aSortOrder, aDateRange, aStartTime, aEndTime]);

  const loadPersonThumbnailIfNeeded = useCallback((personId) => {
    if (!personId) return;
    if (personThumbnailsRef.current[personId] !== undefined || thumbnailRequestsRef.current.has(personId)) {
      return;
    }
    thumbnailRequestsRef.current.add(personId);
    (async () => {
      try {
        const faces = await getPersonFaces(personId);
        const faceList = Array.isArray(faces) ? faces : [];
        const preferredFace = faceList.find((face) => !face.deleted_at) || faceList[0];
        if (preferredFace) {
          const blob = await getFaceImage(preferredFace.face_id);
          const dataUrl = await blobToDataUrl(blob);
          setPersonThumbnails((prev) => ({ ...prev, [personId]: dataUrl }));
        } else {
          setPersonThumbnails((prev) => ({ ...prev, [personId]: null }));
        }
      } catch (err) {
        console.error('Failed to load attendance thumbnail:', err);
        setPersonThumbnails((prev) => ({ ...prev, [personId]: null }));
      } finally {
        thumbnailRequestsRef.current.delete(personId);
      }
    })();
  }, []);

  const handleRequestAttendanceEventImages = useCallback((events = []) => {
    if (!Array.isArray(events) || events.length === 0) return;

    const auditGroups = new Map();
    const attendanceImageIds = new Set();

    events.forEach((evt) => {
      if (!evt) return;
      const cacheKeys = getAttendanceEventImageCacheKeys(evt);
      const fetchAuditId = cacheKeys.find((key) => !key.startsWith('attendance:'));
      if (fetchAuditId) {
        if (!auditGroups.has(fetchAuditId)) {
          auditGroups.set(fetchAuditId, new Set());
        }
        cacheKeys.forEach((key) => auditGroups.get(fetchAuditId).add(key));
        return;
      }
      if (evt.image_available && evt.attendance_id) {
        attendanceImageIds.add(String(evt.attendance_id).trim());
      }
    });

    auditGroups.forEach((cacheKeys, fetchAuditId) => {
      const keys = [...cacheKeys];
      const alreadyLoaded = keys.every((key) => isValidCachedSnapshot(attendanceSnapshotsRef.current[key]));
      if (alreadyLoaded) return;
      if (keys.some((key) => attendanceSnapshotRequestsRef.current.has(key))) return;

      keys.forEach((key) => attendanceSnapshotRequestsRef.current.add(key));
      (async () => {
        try {
          const blob = await getAuditImageBlob(fetchAuditId);
          const dataUrl = await blobToDataUrl(blob);
          const entries = Object.fromEntries(keys.map((key) => [key, dataUrl]));
          setAttendanceSnapshots((prev) => mergeAttendanceSnapshotCache(prev, entries));
        } catch (err) {
          console.error('Failed to load attendance snapshot:', err);
          const entries = {};
          keys.forEach((key) => {
            if (!isValidCachedSnapshot(attendanceSnapshotsRef.current[key])) {
              entries[key] = null;
            }
          });
          if (Object.keys(entries).length > 0) {
            setAttendanceSnapshots((prev) => mergeAttendanceSnapshotCache(prev, entries));
          }
        } finally {
          keys.forEach((key) => attendanceSnapshotRequestsRef.current.delete(key));
        }
      })();
    });

    attendanceImageIds.forEach((attendanceId) => {
      if (!attendanceId) return;
      const cacheKey = `attendance:${attendanceId}`;
      if (isValidCachedSnapshot(attendanceSnapshotsRef.current[cacheKey]) || attendanceSnapshotRequestsRef.current.has(cacheKey)) return;
      attendanceSnapshotRequestsRef.current.add(cacheKey);
      (async () => {
        try {
          const blob = await getAttendanceImageBlob(attendanceId);
          const dataUrl = await blobToDataUrl(blob);
          setAttendanceSnapshots((prev) => mergeAttendanceSnapshotCache(prev, { [cacheKey]: dataUrl }));
        } catch (err) {
          console.error('Failed to load attendance image:', err);
          if (!isValidCachedSnapshot(attendanceSnapshotsRef.current[cacheKey])) {
            setAttendanceSnapshots((prev) => mergeAttendanceSnapshotCache(prev, { [cacheKey]: null }));
          }
        } finally {
          attendanceSnapshotRequestsRef.current.delete(cacheKey);
        }
      })();
    });
  }, []);

  const filteredAttendanceSummaries = useMemo(() => {
    if (!aPremisesFilter) return aSummaries;
    return aSummaries.filter((summary) => {
      const events = summary.recent_events || [];
      if (events.length === 0) return false;
      // Include Missing In/Out placeholders: latest Missing Out means out of premises.
      const sortedEvents = [...events].sort((a, b) => {
        const aTime = new Date(a?.created_at || 0).getTime();
        const bTime = new Date(b?.created_at || 0).getTime();
        return (Number.isNaN(aTime) ? 0 : aTime) - (Number.isNaN(bTime) ? 0 : bTime);
      });
      const mostRecentEvent = sortedEvents[sortedEvents.length - 1];
      if (aPremisesFilter === 'in') return mostRecentEvent?.action === 'check_in';
      if (aPremisesFilter === 'out') return mostRecentEvent?.action === 'check_out';
      return true;
    });
  }, [aSummaries, aPremisesFilter]);

  const presentPeopleCount = Number(aPagination?.total || 0);

  const doesAttendanceFilterMatchState = useCallback((attendanceFilterValue) => {
    if (!attendanceFilterValue) return true;
    if (attendanceFilterValue === 'total_in') return aAction === 'check_in' && !aPremisesFilter && !aLateFilter;
    if (attendanceFilterValue === 'total_out') return aAction === 'check_out' && !aPremisesFilter && !aLateFilter;
    if (attendanceFilterValue === 'in_premises') return !aAction && aPremisesFilter === 'in' && !aLateFilter;
    if (attendanceFilterValue === 'out_premises') return !aAction && aPremisesFilter === 'out' && !aLateFilter;
    if (attendanceFilterValue === 'late_entries') return !aAction && !aPremisesFilter && aLateFilter;
    return true;
  }, [aAction, aPremisesFilter, aLateFilter]);
  
  // Handle navigation from PieChartCard - apply filter from URL params or location state
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const urlFilter = query.get('filter');
    const urlDate = query.get('date');
    const stateFilter = location.state?.filter;
    const stateDate = location.state?.selectedDate;
    const attendanceFilter = location.state?.attendanceFilter;
    const attendanceNavKey = attendanceFilter
      ? `${attendanceFilter}|${location.state?.timestamp || ''}|${location.key || ''}`
      : '';
  
    console.log('Navigation handler triggered:', { 
      urlFilter, 
      urlDate, 
      stateFilter, 
      stateDate,
    });
  
    // Set attendance filters (for Attendance tab)
    if (attendanceFilter) {
      if (attendanceNavFilterKeyRef.current !== attendanceNavKey) {
        attendanceNavFilterKeyRef.current = attendanceNavKey;
        attendanceNavFilterAppliedRef.current = false;
      }
  
      // Apply dashboard attendance filter only once per navigation intent.
      if (!attendanceNavFilterAppliedRef.current) {
        if (attendanceFilter === 'total_in') {
          setAAction('check_in');
          setAPremisesFilter('');
          setALateFilter(false);
        } else if (attendanceFilter === 'total_out') {
          setAAction('check_out');
          setAPremisesFilter('');
          setALateFilter(false);
        } else if (attendanceFilter === 'in_premises') {
          setAAction('');
          setAPremisesFilter('in');
          setALateFilter(false);
        } else if (attendanceFilter === 'out_premises') {
          setAAction('');
          setAPremisesFilter('out');
          setALateFilter(false);
        } else if (attendanceFilter === 'late_entries') {
          setAAction('');
          setAPremisesFilter('');
          setALateFilter(!!lateEntryTrackingEnabled);
        } else {
          attendanceNavFilterAppliedRef.current = true;
        }
      }
    } else {
      attendanceNavFilterKeyRef.current = '';
      attendanceNavFilterAppliedRef.current = true;
    }
  }, [location.search, location.state, location.key, location.pathname, lateEntryTrackingEnabled]);
  
  useEffect(() => {
    const attendanceFilter = location.state?.attendanceFilter;
    if (!attendanceFilter) {
      attendanceNavFilterAppliedRef.current = true;
      return;
    }
    if (doesAttendanceFilterMatchState(attendanceFilter)) {
      attendanceNavFilterAppliedRef.current = true;
    }
  }, [location.state?.attendanceFilter, location.state?.timestamp, doesAttendanceFilterMatchState]);
  
  const handleAttendancePageChange = useCallback((_, page) => {
    const newOffset = (page - 1) * aPagination.limit;
    loadAttendanceSummary(aPagination.limit, newOffset);
  }, [aPagination.limit, loadAttendanceSummary]);

  const attendanceExportVisibleFields = useMemo(
    () => attendanceExportFields.filter((field) => field.enabled !== false),
    [attendanceExportFields]
  );

  const moveSelectedField = useCallback((fromIndex, direction) => {
    setAttendanceExportSelectedFields((prev) => {
      const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const toggleAttendanceExportField = useCallback((fieldName, checked) => {
    setAttendanceExportSelectedFields((prev) => {
      if (checked) return prev.includes(fieldName) ? prev : [...prev, fieldName];
      return prev.filter((item) => item !== fieldName);
    });
  }, []);

  const buildExportDateTimeRange = useCallback(() => {
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const selection = attendanceExportDateRange[0] || { startDate: new Date(), endDate: new Date() };
    const startDate = selection.startDate || new Date();
    const endDate = selection.endDate || startDate;
    const [startHour, startMinute] = parseTime(attendanceExportStartTime, 0, 0);
    const [endHour, endMinute] = parseTime(attendanceExportEndTime, 23, 59);

    const localStart = new Date(startDate);
    localStart.setHours(startHour, startMinute, 0, 0);
    const localEnd = new Date(endDate);
    localEnd.setHours(endHour, endMinute, 59, 0);
    if (localStart > localEnd) return { error: 'Start date/time cannot be after end date/time' };

    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startHour,
      startMinute,
      0,
    ));
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);
    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endHour,
      endMinute,
      59,
    ));
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);
    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
    };
  }, [attendanceExportDateRange, attendanceExportEndTime, attendanceExportStartTime]);

  const handleOpenAttendanceExportDialog = useCallback(async (formatType = 'csv') => {
    try {
      setExportAttendanceLoading(true);
      const normalizedFormat = String(formatType || 'csv').toLowerCase() === 'xlsx' ? 'xlsx' : 'csv';
      setAttendanceExportFormat(normalizedFormat);
      const roleParams = employeeTypeFilterToRoleParams(aEmployeeTypeFilter);
      const metadata = await exportAttendanceLogs({ meta_only: true, is_intrusion: false, ...roleParams });
      const allFields = Array.isArray(metadata?.all_fields)
        ? metadata.all_fields
        : (metadata?.fields || []).map((field, idx) => ({
            field,
            label: metadata?.headers?.[idx] || field,
            enabled: true,
          }));
      const initialSelection = allFields
        .filter((field) => field.enabled !== false && field.field !== 'presence_time')
        .map((field) => field.field);
      setAttendanceExportFields(allFields);
      setAttendanceExportSelectedFields(initialSelection.length ? initialSelection : allFields.map((field) => field.field));
      setAttendanceExportPersonName(aPersonName);
      setAttendanceExportAction(aAction);
      setAttendanceExportPremisesFilter(aPremisesFilter);
      setAttendanceExportLateFilter(aLateFilter);
      setAttendanceExportAllEmployeesPresence(false);
      setAttendanceExportFileName('attendance_logs');
      const startDate = aDateRange[0]?.startDate || new Date();
      const endDate = aDateRange[0]?.endDate || startDate;
      setAttendanceExportDateRange([{ startDate, endDate, key: 'selection' }]);
      setAttendanceExportStartTime(aStartTime || '00:00');
      setAttendanceExportEndTime(aEndTime || '23:59');
      setAttendanceExportDatePickerAnchor(null);
      setAttendanceExportDialogOpen(true);
    } catch (error) {
      console.error('Failed to prepare attendance export:', error);
      show(error?.message || 'Failed to prepare attendance export', 'error');
    } finally {
      setExportAttendanceLoading(false);
    }
  }, [aAction, aDateRange, aEndTime, aLateFilter, aPersonName, aPremisesFilter, aStartTime, show]);

  const handleExportAttendance = useCallback(async () => {
    try {
      setExportAttendanceLoading(true);
      const visibleFields = attendanceExportFields.filter((field) => field.enabled !== false);
      const fieldByName = new Map(visibleFields.map((field) => [field.field, field]));
      const exportSelectedFields = attendanceExportSelectedFields.filter((field) => fieldByName.has(field));
      if (!exportSelectedFields.length) {
        show('Select at least one field to export', 'error');
        return;
      }
      const range = buildExportDateTimeRange();
      if (range?.error) {
        show(range.error, 'error');
        return;
      }
      const { formattedStart, formattedEnd } = range;
      const jsonData = await exportAttendanceLogs({
        person_name: attendanceExportAllEmployeesPresence ? '' : attendanceExportPersonName.trim(),
        action: attendanceExportAction,
        start_time: formattedStart,
        end_time: formattedEnd,
        premises_filter: attendanceExportPremisesFilter,
        late_filter: attendanceExportLateFilter,
        selected_fields: exportSelectedFields,
        include_all_employees_presence: attendanceExportAllEmployeesPresence,
        is_intrusion: false,
        ...employeeTypeFilterToRoleParams(aEmployeeTypeFilter),
      });

      const selectedFieldDefs = exportSelectedFields
        .map((field) => fieldByName.get(field))
        .filter(Boolean);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const safeFileName = (attendanceExportFileName || 'attendance_logs').trim() || 'attendance_logs';
      const fields = selectedFieldDefs.map((field) => field.field);
      const headers = selectedFieldDefs.map((field) => field.label || field.field);

      if (attendanceExportFormat === 'xlsx') {
        downloadAttendanceXlsxFile(jsonData, fields, headers, safeFileName);
      } else {
        const csvContent = convertJsonToCsv(jsonData, fields, headers);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${safeFileName}_${timestamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      setAttendanceExportDialogOpen(false);
      show(`Attendance logs exported successfully as ${attendanceExportFormat.toUpperCase()} (${jsonData?.total_records || 0} records)`, 'success');
    } catch (error) {
      console.error('Failed to export attendance logs:', error);
      show(error?.message || 'Failed to export attendance logs', 'error');
    } finally {
      setExportAttendanceLoading(false);
    }
  }, [
    attendanceExportAction,
    attendanceExportAllEmployeesPresence,
    attendanceExportFields,
    attendanceExportFileName,
    attendanceExportLateFilter,
    attendanceExportPersonName,
    attendanceExportPremisesFilter,
    attendanceExportSelectedFields,
    attendanceExportFormat,
    buildExportDateTimeRange,
    show,
  ]);

  const fetchEmployeesForCompany = useCallback(async (requestedCompanyId) => {
    const cacheKey = requestedCompanyId || 'all';
    const cached = employeesCacheRef.current[cacheKey];
    if (cached && Array.isArray(cached)) {
      setEmployees(cached);
      setEmployeesLoading(false);
      return;
    }
    if (employeesRequestRef.current === cacheKey) return;

    setEmployeesLoading(true);
    employeesRequestRef.current = cacheKey;
    try {
      const pageSize = 200;
      let page = 1;
      let totalPages = 1;
      const merged = [];
      while (page <= totalPages) {
        const res = await fetchHrmsEmployees({
          page,
          limit: pageSize,
          companyId: requestedCompanyId,
        });
        const records = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.employees)
            ? res.employees
            : Array.isArray(res?.records)
              ? res.records
              : Array.isArray(res?.list)
                ? res.list
                : Array.isArray(res?.items)
                  ? res.items
                  : [];
        merged.push(...records);
        const meta = res?.meta || res?.pagination || {};
        totalPages = parseInt(meta.count ?? meta.total_pages ?? 1, 10) || 1;
        page += 1;
      }
      const unique = [];
      const seen = new Set();
      merged.forEach((item) => {
        const normalized = normalizeEmployeeOption(item);
        if (!normalized || seen.has(normalized.person_id)) return;
        seen.add(normalized.person_id);
        unique.push(normalized);
      });
      setEmployees(unique);
      employeesCacheRef.current[cacheKey] = unique;
    } catch (error) {
      console.error('Failed to load employees:', error);
      show('Failed to load employees', 'error');
    } finally {
      setEmployeesLoading(false);
      employeesRequestRef.current = null;
    }
  }, [show]);

  const loadEmployees = useCallback(
    async (...args) => {
      const companyIdOverride = args[0];
      const explicit = args.length > 0;

      let requestedCompanyId = null;
      if (explicit) {
        if (companyIdOverride === '') {
          setEmployees([]);
          setEmployeesLoading(false);
          return;
        }
        requestedCompanyId = companyIdOverride ? String(companyIdOverride) : null;
      } else if (markAttendanceDialog) {
        if (companyOptions.length > 1) {
          const id = markAttendanceCompanyId ? String(markAttendanceCompanyId) : '';
          if (!id) {
            setEmployees([]);
            setEmployeesLoading(false);
            return;
          }
          requestedCompanyId = id;
        } else {
          const fallback =
            (markAttendanceCompanyId && String(markAttendanceCompanyId)) ||
            (companyOptions[0]?.company_id && String(companyOptions[0].company_id)) ||
            '';
          requestedCompanyId = fallback || null;
        }
      } else {
        requestedCompanyId = null;
      }

      await fetchEmployeesForCompany(requestedCompanyId);
    },
    [companyOptions, fetchEmployeesForCompany, markAttendanceCompanyId, markAttendanceDialog]
  );

  useEffect(() => {
    if (!markAttendanceDialog) return;
    let cancelled = false;
    const isMissingEntryMode = markAttendanceContext?.source === 'missing_entry';
    (async () => {
      try {
        const res = await getCompanyPickerConfig(permissions);
        const configs = res?.configs || (res ? [res] : []);
        if (cancelled) return;
        setCompanyOptions(configs);

        if (configs.length > 1 && !isMissingEntryMode) {
          setEmployees([]);
          setEmployeesLoading(false);
          return;
        }

        const singleId = configs.length === 1
          ? (configs[0]?.company_id || '')
          : (isMissingEntryMode ? (markAttendanceCompanyId || '') : '');
        if (singleId) {
          setMarkAttendanceCompanyId(singleId);
        }
        await fetchEmployeesForCompany(singleId || null);
      } catch (err) {
        console.debug('Could not load company configs for mark attendance', err);
        if (!cancelled) {
          setCompanyOptions([]);
          await fetchEmployeesForCompany(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [markAttendanceDialog, fetchEmployeesForCompany, markAttendanceContext, markAttendanceCompanyId, permissions]);

  useEffect(() => {
    if (!markAttendanceDialog) return;
    if (companyOptions.length <= 1) return;
    if (!markAttendanceCompanyId) {
      setEmployees([]);
      return;
    }
    loadEmployees(markAttendanceCompanyId);
  }, [markAttendanceCompanyId, companyOptions.length, markAttendanceDialog, loadEmployees]);

  const handleOpenMarkAttendance = useCallback(() => {
    setMarkAttendanceContext(null);
    setMarkAttendanceCompanyId('');
    setMarkAttendancePersonId('');
    setMarkAttendancePersonName('');
    setMarkAttendanceCheckIn('');
    setMarkAttendanceCheckOut('');
    setMarkAttendanceRemark('');
    setAttendanceCalendarOpen(false);
    setCalendarType('checkin');
    setSelectedAttendanceDate(new Date());
    setMarkAttendanceDialog(true);
  }, []);

  const handleCloseMarkAttendance = useCallback(() => {
    setMarkAttendanceDialog(false);
    setMarkAttendanceContext(null);
    setMarkAttendanceCompanyId('');
    setMarkAttendancePersonId('');
    setMarkAttendancePersonName('');
    setMarkAttendanceCheckIn('');
    setMarkAttendanceCheckOut('');
    setMarkAttendanceRemark('');
    setAttendanceCalendarOpen(false);
  }, []);

  const handleOpenMissingEntryMarkAttendance = useCallback((payload = {}) => {
    const personId = String(payload.personId || '').trim();
    if (!personId) {
      show('Missing entry person is unavailable', 'error');
      return;
    }
    const action = payload.missingAction === 'check_in' ? 'check_in' : 'check_out';
    const parseUtcish = (value) => {
      const raw = String(value || '').trim();
      if (!raw) return null;
      const hasTz = /([+-]\d{2}:?\d{2}|Z)$/i.test(raw);
      const iso = hasTz ? raw : `${raw}Z`;   // treat backend no-TZ as UTC
      const dt = new Date(iso);
      return Number.isNaN(dt.getTime()) ? null : dt;
    };
    
    const start = parseUtcish(payload.windowStart);
    const end = parseUtcish(payload.windowEnd);
    const validStart = start && !Number.isNaN(start.getTime()) ? start : null;
    const validEnd = end && !Number.isNaN(end.getTime()) ? end : null;
    if (!validStart || !validEnd || validStart > validEnd) {
      show('Invalid missing-entry time window', 'error');
      return;
    }

    setMarkAttendanceContext({
      source: 'missing_entry',
      allowedAction: action,
      windowStart: validStart.toISOString(),
      windowEnd: validEnd.toISOString(),
      lockCompany: true,
      lockEmployee: true,
      employeeCode: payload.employeeCode || '',
    });
    setMarkAttendanceCompanyId(String(payload.companyId || '').trim());
    setMarkAttendancePersonId(personId);
    setMarkAttendancePersonName(payload.personName || '');
    setMarkAttendanceCheckIn('');
    setMarkAttendanceCheckOut('');
    setMarkAttendanceRemark('');
    setAttendanceCalendarOpen(false);
    setCalendarType(action === 'check_in' ? 'checkin' : 'checkout');
    setSelectedAttendanceDate(new Date(validStart));
    setMarkAttendanceDialog(true);
  }, [show]);

  const handleMarkAttendanceSubmit = useCallback(async () => {
    const isMissingEntryMode = markAttendanceContext?.source === 'missing_entry';
    const constrainedAction = isMissingEntryMode
      ? (markAttendanceContext?.allowedAction === 'check_in' ? 'check_in' : 'check_out')
      : null;
    if (!markAttendancePersonId || (!markAttendanceCheckIn && !markAttendanceCheckOut)) {
      show('Please select an employee and provide at least check-in or check-out time', 'error');
      return;
    }
    if (!markAttendanceRemark.trim()) {
      show('Please provide a remark for manual attendance', 'error');
      return;
    }
    if (isMissingEntryMode) {
      if (constrainedAction === 'check_in' && !markAttendanceCheckIn) {
        show('Please select check-in time for this missing entry', 'error');
        return;
      }
      if (constrainedAction === 'check_out' && !markAttendanceCheckOut) {
        show('Please select check-out time for this missing entry', 'error');
        return;
      }
    }
    try {
      setMarkAttendanceSubmitting(true);
      let formattedRemark = markAttendanceRemark.trim();
      if (!formattedRemark.endsWith('.')) formattedRemark += '.';
      const selected = employees.find((employee) => employee.person_id === markAttendancePersonId);
      const checkInUTC = markAttendanceCheckIn ? new Date(markAttendanceCheckIn).toISOString() : null;
      const checkOutUTC = markAttendanceCheckOut ? new Date(markAttendanceCheckOut).toISOString() : null;
      const constraintStart = markAttendanceContext?.windowStart ? new Date(markAttendanceContext.windowStart) : null;
      const constraintEnd = markAttendanceContext?.windowEnd ? new Date(markAttendanceContext.windowEnd) : null;
      const hasConstraintRange =
        isMissingEntryMode &&
        constraintStart &&
        !Number.isNaN(constraintStart.getTime()) &&
        constraintEnd &&
        !Number.isNaN(constraintEnd.getTime());
      const assertInAllowedWindow = (isoText) => {
        if (!hasConstraintRange || !isoText) return true;
        const candidate = new Date(isoText);
        if (Number.isNaN(candidate.getTime())) return false;
        return candidate >= constraintStart && candidate <= constraintEnd;
      };
      if (isMissingEntryMode) {
        const candidateIso = constrainedAction === 'check_in' ? checkInUTC : checkOutUTC;
        if (!candidateIso || !assertInAllowedWindow(candidateIso)) {
          show('Attendance time must be within the missing-entry search window', 'error');
          return;
        }
      }

      if (checkInUTC && (!isMissingEntryMode || constrainedAction === 'check_in')) {
        const checkInResult = await markAttendance({
          person_id: markAttendancePersonId,
          action: 'check_in',
          person_name: markAttendancePersonName || selected?.name || '',
          employee_code: selected?.employee_code || '',
          timestamp: checkInUTC,
          remark: formattedRemark,
        });
        if (!checkInResult?.attendance_id && !checkInResult?.success) {
          show(checkInResult?.message || checkInResult?.detail || 'Failed to mark check-in', 'error');
          return;
        }
      }

      if (checkOutUTC && (!isMissingEntryMode || constrainedAction === 'check_out')) {
        const checkOutResult = await markAttendance({
          person_id: markAttendancePersonId,
          action: 'check_out',
          person_name: markAttendancePersonName || selected?.name || '',
          employee_code: selected?.employee_code || '',
          timestamp: checkOutUTC,
          remark: formattedRemark,
        });
        if (!checkOutResult?.attendance_id && !checkOutResult?.success) {
          const prefix = checkInUTC ? 'Check-in marked but check-out failed: ' : 'Failed to mark check-out: ';
          show(prefix + (checkOutResult?.message || checkOutResult?.detail || 'Unknown error'), checkInUTC ? 'warning' : 'error');
          if (!checkInUTC) return;
        }
      }

      handleCloseMarkAttendance();
      if (isMissingEntryMode) {
        setMissingEntrySearchModalOpen(false);
        setMissingEntrySearchProps({ missingEvent: null, personThumbnail: null, recentEvents: [], personId: null });
      }
      show('Attendance marked successfully', 'success');
      loadAttendanceSummary(aPagination.limit, aPagination.offset);
    } catch (error) {
      console.error('Mark attendance error:', error);
      show(error?.message || 'Failed to mark attendance', 'error');
    } finally {
      setMarkAttendanceSubmitting(false);
    }
  }, [
    aPagination.limit,
    aPagination.offset,
    employees,
    handleCloseMarkAttendance,
    loadAttendanceSummary,
    markAttendanceContext,
    markAttendanceCheckIn,
    markAttendanceCheckOut,
    markAttendancePersonId,
    markAttendancePersonName,
    markAttendanceRemark,
    show,
  ]);

  const handleMarkAttendanceSubmitWithConfirm = useCallback(async () => {
    const approved = await confirm({
      title: 'Confirm Mark Attendance',
      message: 'Are you sure you want to mark this attendance entry?',
      confirmLabel: 'Mark Attendance',
      danger: false,
    });
    if (!approved) return;
    await handleMarkAttendanceSubmit();
  }, [confirm, handleMarkAttendanceSubmit]);

  const handleExportAttendanceWithConfirm = useCallback(async () => {
    const approved = await confirm({
      title: 'Confirm Export',
      message: `Are you sure you want to export attendance as ${attendanceExportFormat.toUpperCase()}?`,
      confirmLabel: `Export ${attendanceExportFormat.toUpperCase()}`,
      danger: false,
    });
    if (!approved) return;
    await handleExportAttendance();
  }, [confirm, attendanceExportFormat, handleExportAttendance]);

  const openTimingDialog = useCallback(({ log, stack = [] }) => {
    if (!log) return;
    setTimingDialog({
      open: true,
      log,
      stack: Array.isArray(stack) ? stack : [],
    });
  }, []);

  const closeTimingDialog = useCallback(() => {
    setTimingDialog({ open: false, log: null, stack: [] });
  }, []);

  const handleOpenMissingEntrySearch = useCallback((missingEvent, personThumbnail, recentEvents, personId) => {
    if (!missingEvent?.is_missing_entry) {
      show('Invalid missing entry data', 'error');
      return;
    }
    setMissingEntrySearchProps({ missingEvent, personThumbnail, recentEvents, personId });
    setMissingEntrySearchModalOpen(true);
  }, [show]);

  const handleCloseMissingEntrySearch = useCallback(() => {
    setMissingEntrySearchModalOpen(false);
    setMissingEntrySearchProps({ missingEvent: null, personThumbnail: null, recentEvents: [], personId: null });
  }, []);

  const handleMissingEntrySearchLogClick = useCallback(({ log, stack }) => {
    if (!log) return;
    const image = Array.isArray(stack) && stack[0] ? stack[0].image : null;
    const capturedTs = getCapturedTimestamp(log);
    const requestSentTs = getRequestSentTimestamp(log);
    const apiSubmitTs = getApiSubmitTimestamp(log);
    const apiReceivedTs = getApiReceivedTimestamp(log);
    const logWithStack = {
      ...log,
      capturedTs,
      requestSentTs,
      apiSubmitTs,
      apiReceivedTs,
    };
    openTimingDialog({
      log: logWithStack,
      stack: [{ log: logWithStack, image }],
    });
  }, [openTimingDialog]);

  const openRevokeConfirmDialog = useCallback((topLog, bucketLogs = []) => {
    const ref = resolveBucketAttendanceRef(topLog, bucketLogs);
    const entries = [topLog, ...(Array.isArray(bucketLogs) ? bucketLogs : [])].filter(Boolean);
    const sourceLog = entries.find((entry) => String(entry?.audit_id || '') === String(ref.sourceAuditId || '')) || topLog;
    const userName = sourceLog?.recognized_name || topLog?.recognized_name || 'Unknown';

    setRevokeConfirmDialog({
      open: true,
      topLog,
      bucketLogs: Array.isArray(bucketLogs) ? bucketLogs : [],
      userName,
    });
  }, []);

  const closeRevokeConfirmDialog = useCallback(() => {
    setRevokeConfirmDialog({
      open: false,
      topLog: null,
      bucketLogs: [],
      userName: '',
    });
  }, []);

  const handleRevokeAttendance = useCallback(async (topLog, bucketLogs = []) => {
    const ref = resolveBucketAttendanceRef(topLog, bucketLogs);
    const fallbackAuditId = ref.sourceAuditId || topLog?.audit_id || null;
    if (!ref.attendanceId && !fallbackAuditId) {
      show('Attendance record is not linked on this audit tile', 'warning');
      return;
    }
    if (ref.revoked) {
      show('Attendance is already revoked for this tile', 'info');
      return;
    }

    const loadingKey = fallbackAuditId || ref.attendanceId;
    setRevokingAuditIds((prev) => {
      const next = new Set(prev);
      next.add(loadingKey);
      return next;
    });

    try {
      let response = null;
      let resolvedAttendanceId = ref.attendanceId || null;
      if (resolvedAttendanceId) {
        try {
          response = await deleteAttendanceLog(resolvedAttendanceId, fallbackAuditId || '');
        } catch (deleteErr) {
          const msg = String(deleteErr?.message || '').toLowerCase();
          const shouldTryFallback =
            Boolean(fallbackAuditId) &&
            (msg.includes('attendance log not found') || msg.includes('404'));
          if (!shouldTryFallback) throw deleteErr;
          response = await revokeAttendanceByAudit(fallbackAuditId);
          resolvedAttendanceId = response?.attendance_id || resolvedAttendanceId;
        }
      } else {
        response = await revokeAttendanceByAudit(fallbackAuditId);
        resolvedAttendanceId = response?.attendance_id || null;
      }

      if (response?.success === false) {
        show(response?.detail || response?.message || 'Failed to revoke attendance', 'error');
        return;
      }

      const revokedByName = response?.updated_by_name || response?.revoked_by_name || null;
      const resolvedSourceAuditId = response?.source_audit_id || fallbackAuditId || null;

      if (!resolvedAttendanceId) {
        show('Attendance revoked but attendance id not returned', 'warning');
        return;
      }

      setTimingDialog((prev) => {
        if (!prev?.log) return prev;
        const updatedLog = String(prev.log?.audit_id || '') === String(resolvedSourceAuditId || '')
          ? markLogAttendanceRevoked(prev.log, resolvedAttendanceId, revokedByName)
          : prev.log;
        const updatedStack = Array.isArray(prev.stack)
          ? prev.stack.map((item) => {
              const itemAuditId = String(item?.log?.audit_id || '');
              if (itemAuditId !== String(resolvedSourceAuditId || '')) return item;
              return {
                ...item,
                log: markLogAttendanceRevoked(item.log, resolvedAttendanceId, revokedByName),
              };
            })
          : prev.stack;
        return {
          ...prev,
          log: updatedLog,
          stack: updatedStack,
        };
      });

      show('Attendance revoked successfully', 'success');
      await loadAttendanceSummary(aPagination.limit, aPagination.offset);
      return true;
    } catch (error) {
      console.error('Revoke attendance error:', error);
      show(error?.message || 'Failed to revoke attendance', 'error');
      return false;
    } finally {
      setRevokingAuditIds((prev) => {
        const next = new Set(prev);
        next.delete(loadingKey);
        return next;
      });
    }
  }, [show, loadAttendanceSummary, aPagination.limit, aPagination.offset]);

  const confirmRevokeAttendance = useCallback(async () => {
    const topLog = revokeConfirmDialog?.topLog;
    const bucketLogs = revokeConfirmDialog?.bucketLogs || [];
    if (!topLog) {
      closeRevokeConfirmDialog();
      return;
    }
    const succeeded = await handleRevokeAttendance(topLog, bucketLogs);
    closeRevokeConfirmDialog();
    if (succeeded) {
      setTimingDialog({ open: false, log: null, stack: [] });
    }
  }, [closeRevokeConfirmDialog, handleRevokeAttendance, revokeConfirmDialog]);

  const handleOpenRetryMatchDialog = useCallback(async (event, log, image, stackImages = []) => {
    if (event) event.stopPropagation();
    if (!log?.audit_id) return;

    try {
      const logThreshold = Number(log?.similarity_threshold);
      const defaultThreshold = Number.isFinite(logThreshold)
        ? Math.max(0, Math.min(1, logThreshold))
        : 0.6;
      setRetryMatchThreshold(String(defaultThreshold));
      retryMatchThresholdRef.current = defaultThreshold;

      const existingImages = [];
      const missingItems = [];
      if (image) {
        existingImages.push({ log, image, isMain: true, uniqueId: log.audit_id || `main-${Date.now()}` });
      } else if (log.audit_id) {
        missingItems.push({ log, isMain: true, uniqueId: log.audit_id });
      }

      if (Array.isArray(stackImages) && stackImages.length > 0) {
        stackImages.forEach((stackItem, stackIdx) => {
          if (!stackItem?.log?.audit_id || stackItem.log.audit_id === log.audit_id) return;
          if (stackItem.image) {
            existingImages.push({
              log: stackItem.log,
              image: stackItem.image,
              isMain: false,
              uniqueId: stackItem.log.audit_id || `stack-${Date.now()}-${stackIdx}`,
            });
          } else {
            missingItems.push({
              log: stackItem.log,
              isMain: false,
              uniqueId: stackItem.log.audit_id || `stack-${Date.now()}-${stackIdx}`,
            });
          }
        });
      }

      setRetryMatchDialogOpen(true);
      setRetryMatchResultData(null);
      setRetryMatchResultLoading(false);
      setRetryMatchStackImages(existingImages);
      const firstExisting = existingImages[0];
      setRetryMatchSelectedAuditId(firstExisting?.uniqueId || null);
      setRetryMatchAudit(firstExisting?.log || log);

      if (!missingItems.length) {
        if (!existingImages.length) show('No image available for retry match', 'warning');
        return;
      }

      setRetryMatchImageLoading(true);
      const fetched = await Promise.all(
        missingItems.map(async (item) => {
          try {
            const blob = await getAuditImageBlob(item.log.audit_id);
            const dataUrl = await blobToDataUrl(blob);
            return { ...item, image: dataUrl };
          } catch (err) {
            console.error(`Error loading retry image ${item.log.audit_id}:`, err);
            return null;
          }
        })
      );

      const fetchedImages = fetched.filter(Boolean);
      setRetryMatchStackImages((prev) => {
        const map = new Map();
        (prev || []).forEach((it) => map.set(String(it?.log?.audit_id || it?.uniqueId || ''), it));
        fetchedImages.forEach((it) => map.set(String(it?.log?.audit_id || it?.uniqueId || ''), it));
        return Array.from(map.values());
      });
      if (!firstExisting && fetchedImages.length > 0) {
        setRetryMatchSelectedAuditId(fetchedImages[0].uniqueId);
        setRetryMatchAudit(fetchedImages[0].log);
      }
    } catch (error) {
      console.error('Error preparing retry match dialog:', error);
      show('Failed to open retry match', 'error');
    } finally {
      setRetryMatchImageLoading(false);
    }
  }, [show]);

  const handleRetryMatchDialogClose = useCallback(() => {
    if (matchingLoading || retryMatchAttendanceSubmitting || retryMatchResultLoading) return;
    setRetryMatchDialogOpen(false);
    setRetryMatchAudit(null);
    setRetryMatchThreshold('0.6');
    retryMatchThresholdRef.current = 0.6;
    setRetryMatchStackImages([]);
    setRetryMatchSelectedAuditId(null);
    setRetryMatchImageLoading(false);
    setRetryMatchResultData(null);
    setRetryMatchResultLoading(false);
  }, [matchingLoading, retryMatchAttendanceSubmitting, retryMatchResultLoading]);

  const getPersonProfileImage = useCallback(async (personId) => {
    const pid = String(personId || '').trim();
    if (!pid) return null;
    try {
      const faces = await getPersonFaces(pid);
      const faceList = Array.isArray(faces) ? faces : [];
      const activeFaces = faceList.filter((f) => !f.deleted_at);
      const sortByPoseDesc = (a, b) => Number(b?.pose_quality || 0) - Number(a?.pose_quality || 0);
      const activeFrontFaces = activeFaces
        .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
        .sort(sortByPoseDesc);
      const fallbackFrontFaces = faceList
        .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
        .sort(sortByPoseDesc);
      const preferredFace =
        activeFrontFaces[0] ||
        fallbackFrontFaces[0] ||
        activeFaces.sort(sortByPoseDesc)[0] ||
        faceList.sort(sortByPoseDesc)[0];
      if (!preferredFace?.face_id) return null;
      const blob = await getFaceImage(preferredFace.face_id);
      return await blobToDataUrl(blob);
    } catch (_) {
      return null;
    }
  }, []);

  const handleRetryMatchContinue = useCallback(async () => {
    const parsed = Number(retryMatchThresholdRef.current ?? retryMatchThreshold);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
      show('Similarity threshold must be between 0.0 and 1.0', 'error');
      return;
    }
    setRetryMatchThreshold(String(parsed));
    if (Array.isArray(retryMatchStackImages) && retryMatchStackImages.length > 1 && !retryMatchSelectedAuditId) {
      show('Please select an image for retry match', 'warning');
      return;
    }
    if (Array.isArray(retryMatchStackImages) && retryMatchStackImages.length > 0 && retryMatchSelectedAuditId) {
      const selected = retryMatchStackImages.find((item) => String(item.uniqueId) === String(retryMatchSelectedAuditId));
      if (selected?.log?.audit_id) {
        setRetryMatchAudit(selected.log);
      }
    }

    const selected = Array.isArray(retryMatchStackImages) && retryMatchStackImages.length > 0
      ? retryMatchStackImages.find((item) => String(item.uniqueId || '') === String(retryMatchSelectedAuditId || '')) || retryMatchStackImages[0]
      : null;
    const selectedAudit = selected?.log || retryMatchAudit;
    if (!selectedAudit?.audit_id) return;

    try {
      setRetryMatchResultLoading(true);
      setMatchingLoading(true);

      const result = await matchAuditImage(selectedAudit.audit_id, parsed, false);
      if (!result?.success) {
        show(result?.message || 'Failed to match image', 'error');
        return;
      }

      const employeeImage = await getPersonProfileImage(result.recognized_person_id);
      setRetryMatchResultData({
        auditLog: selectedAudit,
        threshold: parsed,
        auditImage: selected?.image || resolvePresentAuditImage(selectedAudit.audit_id, attendanceSnapshots),
        employeeImage,
        result,
      });
      show(`Preview ready: ${result.recognized_name || 'No match found'}`, 'info');
    } catch (e) {
      show(e.message || 'Failed to match image', 'error');
    } finally {
      setMatchingLoading(false);
      setRetryMatchResultLoading(false);
    }
  }, [
    retryMatchThreshold,
    retryMatchStackImages,
    retryMatchSelectedAuditId,
    retryMatchAudit,
    show,
    getPersonProfileImage,
    attendanceSnapshots,
  ]);

  const handleRetryMatchApply = useCallback(async () => {
    if (!retryMatchResultData?.auditLog?.audit_id) {
      show('Match details are missing', 'error');
      return;
    }
    try {
      setRetryMatchAttendanceSubmitting(true);
      const log = retryMatchResultData.auditLog;
      const threshold = Number(retryMatchResultData?.threshold);
      const commitRes = await matchAuditImage(
        log.audit_id,
        Number.isFinite(threshold) ? threshold : null,
        true,
        retryMatchResultData?.result || null
      );
      if (!commitRes?.success) {
        show(commitRes?.message || 'Failed to apply match', 'error');
        return;
      }

      const nextConfidenceLevel = commitRes?.confidence_level || 'no_match';
      const hasRecognizedPerson = Boolean(commitRes?.recognized_person_id);
      const isSuccessfulMatch =
        hasRecognizedPerson && !['no_match', 'no_face', 'error'].includes(String(nextConfidenceLevel || '').toLowerCase());
      const updatedFields = {
        recognized_name: commitRes?.recognized_name || null,
        recognized_person_id: commitRes?.recognized_person_id || null,
        recognized_face_id: commitRes?.recognized_face_id || null,
        confidence_score: typeof commitRes?.confidence_score === 'number' ? commitRes.confidence_score : 0,
        confidence_level: nextConfidenceLevel,
        success: isSuccessfulMatch,
        unmatch_to_match_by: commitRes?.unmatch_to_match_by || null,
      };
      if (Number.isFinite(Number(threshold))) {
        updatedFields.similarity_threshold = threshold;
      }

      setTimingDialog((prev) => {
        if (!prev?.log) return prev;
        return {
          ...prev,
          log: String(prev.log?.audit_id || '') === String(log.audit_id) ? { ...prev.log, ...updatedFields } : prev.log,
          stack: Array.isArray(prev.stack)
            ? prev.stack.map((item) => {
                if (String(item?.log?.audit_id || '') !== String(log.audit_id)) return item;
                return { ...item, log: { ...item.log, ...updatedFields } };
              })
            : prev.stack,
        };
      });

      show('Match updated successfully', 'success');
      handleRetryMatchDialogClose();
      await loadAttendanceSummary(aPagination.limit, aPagination.offset);
    } catch (e) {
      show(e.message || 'Failed to apply match', 'error');
    } finally {
      setRetryMatchAttendanceSubmitting(false);
    }
  }, [
    retryMatchResultData,
    show,
    handleRetryMatchDialogClose,
    loadAttendanceSummary,
    aPagination.limit,
    aPagination.offset,
  ]);

  useEffect(() => {
    if (!retryMatchDialogOpen || retryMatchResultLoading || !retryMatchResultData) return;
    const scrollTarget = retryMatchResultRef.current;
    const scrollContainer = retryMatchContentRef.current;
    if (scrollTarget?.scrollIntoView) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
    }
  }, [retryMatchDialogOpen, retryMatchResultLoading, retryMatchResultData]);

  const timingDialogStack = timingDialog.stack || [];
  const fallbackDialogFrame = timingDialog.log
    ? {
        log: timingDialog.log,
        image: resolvePresentAuditImage(timingDialog.log.audit_id, attendanceSnapshots),
      }
    : null;
  const modalFrames = timingDialogStack.length
    ? timingDialogStack
    : fallbackDialogFrame
      ? [fallbackDialogFrame]
      : [];
  const timingDialogBucketLogs = modalFrames
    .map((frame) => frame?.log)
    .filter(Boolean);
  const activeTimingLog = timingDialog.log || modalFrames[0]?.log || null;
  const timingDialogResultMeta = getResultMeta(activeTimingLog);
  const timingDialogAttendanceRef = activeTimingLog
    ? resolveBucketAttendanceRef(activeTimingLog, timingDialogBucketLogs)
    : { attendanceId: null, sourceAuditId: null, hasAttendance: false, revoked: false };
  const timingDialogIsMatchedTile = Boolean(activeTimingLog?.success) && activeTimingLog?.confidence_level !== 'possible_match';
  const canTimingDialogRevokeAttendance =
    timingDialogIsMatchedTile &&
    timingDialogAttendanceRef.hasAttendance &&
    !timingDialogAttendanceRef.revoked;
  const timingDialogRevokeLoading = Boolean(
    [timingDialogAttendanceRef.sourceAuditId, activeTimingLog?.audit_id, timingDialogAttendanceRef.attendanceId]
      .filter(Boolean)
      .some((key) => revokingAuditIds.has(key))
  );
  const hasTimingDialogTrackAttendance =
    modalFrames.some((frame) => frame?.log?.send_attendance === true) ||
    activeTimingLog?.send_attendance === true;

  return (
    <Container maxWidth="2200" disableGutters sx={{ px: 0 }}>
      <PageHeader
        title="Present"
        subtitle="Track employee attendance"
        icon={null}
        titleAdornment={presentPeopleCount > 0 ? (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={aLoading ? 'Loading...' : `${presentPeopleCount} Present`}
            sx={{ fontWeight: 700, mt: { xs: 0.25, sm: 0.5 } }}
          />
        ) : null}
      />
      <AuditPresentContent
        availableNames={safeAvailableNames}
        aPersonName={aPersonName}
        setAPersonName={setAPersonName}
        aAction={aAction}
        setAAction={setAAction}
        aPremisesFilter={aPremisesFilter}
        setAPremisesFilter={setAPremisesFilter}
        aEmployeeTypeFilter={aEmployeeTypeFilter}
        setAEmployeeTypeFilter={setAEmployeeTypeFilter}
        aLateFilter={aLateFilter}
        setALateFilter={setALateFilter}
        aAttendanceSync={aAttendanceSync}
        setAAttendanceSync={setAAttendanceSync}
        aSortOrder={aSortOrder}
        setASortOrder={setASortOrder}
        setADatePickerAnchor={setADatePickerAnchor}
        aDateRange={aDateRange}
        aDatePickerOpen={aDatePickerOpen}
        aDatePickerAnchor={aDatePickerAnchor}
        isMobile={isMobile}
        primary={PRIMARY}
        setADateRange={setADateRange}
        aStartTime={aStartTime}
        setAStartTime={setAStartTime}
        aEndTime={aEndTime}
        setAEndTime={setAEndTime}
        loadAttendanceSummary={loadAttendanceSummary}
        aPagination={aPagination}
        handleOpenAttendanceExportDialog={handleOpenAttendanceExportDialog}
        exportAttendanceLoading={exportAttendanceLoading}
        aLoading={aLoading}
        handleOpenMarkAttendance={handleOpenMarkAttendance}
        AttendanceCardSkeleton={LocalAttendanceCardSkeleton}
        filteredAttendanceSummaries={filteredAttendanceSummaries}
        personThumbnails={personThumbnails}
        attendanceSnapshots={attendanceSnapshots}
        lateEntryTime={lateEntryTime}
        lateEntryTrackingEnabled={lateEntryTrackingEnabled}
        openTimingDialog={openTimingDialog}
        handleAttendancePageChange={handleAttendancePageChange}
        onMissingEntrySearchClick={handleOpenMissingEntrySearch}
        onLoadAttendanceTimeline={loadAttendanceTimelineForSummary}
        onRequestAttendanceEventImages={handleRequestAttendanceEventImages}
        onRequestPersonThumbnail={loadPersonThumbnailIfNeeded}
      />

      <MissingEntrySearchModal
        open={missingEntrySearchModalOpen}
        missingEvent={missingEntrySearchProps.missingEvent}
        personThumbnail={missingEntrySearchProps.personThumbnail}
        recentEvents={missingEntrySearchProps.recentEvents}
        personId={missingEntrySearchProps.personId}
        onClose={handleCloseMissingEntrySearch}
        onSuccess={() => loadAttendanceSummary(aPagination.limit, aPagination.offset)}
        showMessage={show}
        onLogClick={handleMissingEntrySearchLogClick}
        onOpenManualMarkAttendance={handleOpenMissingEntryMarkAttendance}
      />

      <AuditDialogs
        findPersonSearchIconAuditLogsModalOpen={false}
        handleCloseFindPersonSearchIconAuditLogs={() => {}}
        handleSearchFindPersonSearchIconAuditLogs={() => {}}
        findPersonSearchIconAuditLogsLoading={false}
        findPersonSearchIconAuditLogsSelectedImage={null}
        primary={PRIMARY}
        findPersonSearchIconAuditLogsStackImages={[]}
        findPersonSearchIconAuditLogsSelectedAuditId={null}
        getCapturedTimestamp={getCapturedTimestamp}
        setFindPersonSearchIconAuditLogsSelectedImage={() => {}}
        setFindPersonSearchIconAuditLogsSelectedAuditId={() => {}}
        findPersonSearchIconAuditLogsThreshold={0.3}
        setFindPersonSearchIconAuditLogsThreshold={() => {}}
        attendanceExportDialogOpen={attendanceExportDialogOpen}
        setAttendanceExportDialogOpen={setAttendanceExportDialogOpen}
        handleExportAttendance={handleExportAttendanceWithConfirm}
        exportAttendanceLoading={exportAttendanceLoading}
        attendanceExportFormat={attendanceExportFormat}
        attendanceExportSelectedFields={attendanceExportSelectedFields}
        attendanceExportFileName={attendanceExportFileName}
        setAttendanceExportFileName={setAttendanceExportFileName}
        setAttendanceExportDatePickerAnchor={setAttendanceExportDatePickerAnchor}
        attendanceExportDateRange={attendanceExportDateRange}
        attendanceExportStartTime={attendanceExportStartTime}
        attendanceExportEndTime={attendanceExportEndTime}
        attendanceExportDatePickerOpen={attendanceExportDatePickerOpen}
        attendanceExportDatePickerAnchor={attendanceExportDatePickerAnchor}
        setAttendanceExportDateRange={setAttendanceExportDateRange}
        isMobile={isMobile}
        setAttendanceExportStartTime={setAttendanceExportStartTime}
        setAttendanceExportEndTime={setAttendanceExportEndTime}
        buildExportDateTimeRange={buildExportDateTimeRange}
        show={show}
        availableNames={safeAvailableNames}
        attendanceExportPersonName={attendanceExportPersonName}
        attendanceExportAllEmployeesPresence={attendanceExportAllEmployeesPresence}
        setAttendanceExportPersonName={setAttendanceExportPersonName}
        setAttendanceExportAllEmployeesPresence={setAttendanceExportAllEmployeesPresence}
        setAttendanceExportSelectedFields={setAttendanceExportSelectedFields}
        attendanceExportAction={attendanceExportAction}
        setAttendanceExportAction={setAttendanceExportAction}
        attendanceExportPremisesFilter={attendanceExportPremisesFilter}
        setAttendanceExportPremisesFilter={setAttendanceExportPremisesFilter}
        attendanceExportLateFilter={attendanceExportLateFilter}
        setAttendanceExportLateFilter={setAttendanceExportLateFilter}
        lateEntryTrackingEnabled={lateEntryTrackingEnabled}
        attendanceExportVisibleFields={attendanceExportVisibleFields}
        moveSelectedField={moveSelectedField}
        toggleAttendanceExportField={toggleAttendanceExportField}
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        timingDialog={timingDialog}
        closeTimingDialog={closeTimingDialog}
        timingDialogResultMeta={timingDialogResultMeta}
        hasTimingDialogTrackAttendance={hasTimingDialogTrackAttendance}
        possibleMatchTrainActiveRef={possibleMatchTrainActiveRef}
        setTrainAuditId={null}
        setTrainMenuAnchor={null}
        timingDialogPossibleMatchSelectedId={timingDialogPossibleMatchSelectedId}
        timingDialogPossibleMatchMarkSubmitting={timingDialogPossibleMatchMarkSubmitting}
        employees={safeEmployees}
        sortedEmployees={sortedEmployees}
        openConfirmMarkAttendance={null}
        modalFrames={modalFrames}
        imageCache={attendanceSnapshots}
        ANONYMOUS_IMAGE={ANONYMOUS_IMAGE}
        setTimingDialogPossibleMatchSelectedId={setTimingDialogPossibleMatchSelectedId}
        timingDialogPossibleMatchCandidateImages={timingDialogPossibleMatchCandidateImages}
        onTimingDialogRetryMatchClick={(e) => {
          if (!timingDialog.log?.audit_id) return;
          handleOpenRetryMatchDialog(
            e,
            timingDialog.log,
            resolvePresentAuditImage(timingDialog.log.audit_id, attendanceSnapshots),
            modalFrames
          );
        }}
        onTimingDialogRevokeClick={(e) => {
          e.stopPropagation();
          if (!timingDialog.log) return;
          openRevokeConfirmDialog(timingDialog.log, timingDialogBucketLogs);
        }}
        canTimingDialogRevokeAttendance={canTimingDialogRevokeAttendance}
        timingDialogRevokeLoading={timingDialogRevokeLoading}
        addToEmployeeDialog={addToEmployeeDialog}
        setAddToEmployeeDialog={setAddToEmployeeDialog}
        handleAddToEmployee={() => {}}
        operationLoading={operationLoading}
        selectedPersonId={selectedPersonId}
        companyOptions={companyOptions}
        employeeCompanyId={employeeCompanyId}
        setEmployeeCompanyId={setEmployeeCompanyId}
        setSelectedPersonId={setSelectedPersonId}
        setSelectedName={() => {}}
        setSelectedEmployeeCode={() => {}}
        setSelectedCompanyId={() => {}}
        setSelectedBranchId={() => {}}
        OTHER_COMPANY_OPTION_VALUE={OTHER_COMPANY_OPTION_VALUE}
        employeesLoading={employeesLoading}
        createEmployeeDialog={createEmployeeDialog}
        setCreateEmployeeDialog={setCreateEmployeeDialog}
        handleCreateEmployee={() => {}}
        newPersonId={newPersonId}
        setNewPersonId={setNewPersonId}
        newName={newName}
        setNewName={setNewName}
        presentDialogOpen={presentDialogOpen}
        handleClosePresentDialog={() => setPresentDialogOpen(false)}
        handlePresentSubmit={() => {}}
        presentSubmitting={presentSubmitting}
        presentPersonId={presentPersonId}
        setPresentPersonId={setPresentPersonId}
        setPresentName={() => {}}
        markAttendanceDialog={markAttendanceDialog}
        handleCloseMarkAttendance={handleCloseMarkAttendance}
        handleMarkAttendanceSubmit={handleMarkAttendanceSubmitWithConfirm}
        markAttendanceSubmitting={markAttendanceSubmitting}
        markAttendancePersonId={markAttendancePersonId}
        markAttendancePersonName={markAttendancePersonName}
        markAttendanceCheckIn={markAttendanceCheckIn}
        markAttendanceCheckOut={markAttendanceCheckOut}
        markAttendanceRemark={markAttendanceRemark}
        markAttendanceCompanyId={markAttendanceCompanyId}
        setMarkAttendanceCompanyId={setMarkAttendanceCompanyId}
        setMarkAttendancePersonId={setMarkAttendancePersonId}
        setMarkAttendancePersonName={setMarkAttendancePersonName}
        loadEmployees={loadEmployees}
        calendarType={calendarType}
        setCalendarType={setCalendarType}
        setSelectedAttendanceDate={setSelectedAttendanceDate}
        setAttendanceCalendarOpen={setAttendanceCalendarOpen}
        setMarkAttendanceRemark={setMarkAttendanceRemark}
        attendanceCalendarOpen={attendanceCalendarOpen}
        selectedAttendanceDate={selectedAttendanceDate}
        setMarkAttendanceCheckIn={setMarkAttendanceCheckIn}
        setMarkAttendanceCheckOut={setMarkAttendanceCheckOut}
        markAttendanceContext={markAttendanceContext}
        possibleMatchModal={possibleMatchModal}
        closePossibleMatchModal={() => {}}
        setPossibleMatchSelectedId={() => {}}
        possibleMatchSelectedId={possibleMatchSelectedId}
        possibleMatchMarkSubmitting={possibleMatchMarkSubmitting}
        possibleMatchCandidateImages={possibleMatchCandidateImages}
        confirmMarkAttendanceOpen={confirmMarkAttendanceOpen}
        closeConfirmMarkAttendance={() => {}}
        handleConfirmMarkAttendanceSubmit={() => {}}
        confirmMarkAttendanceSource={confirmMarkAttendanceSource}
        confirmMarkAttendancePersonName={confirmMarkAttendancePersonName}
        trainMenuAnchor={trainMenuAnchor}
        handleTrainClose={() => {}}
        handleOpenAddToEmployee={() => {}}
        handleOpenCreateEmployee={() => {}}
        previewOpen={previewOpen}
        closePreview={() => {}}
        previewUrl={previewUrl}
      />

<Dialog
        open={revokeConfirmDialog.open}
        onClose={closeRevokeConfirmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            maxWidth: 600,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'rgba(239, 68, 68, 0.12)',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BlockIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Revoke Attendance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, px: 3 }}>
          <Box
            sx={{
              borderRadius: 1.5,
              p: 2,
              bgcolor: '#fff7ed',
              border: '1px solid #fed7aa',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: '#9a3412', mb: 0.75 }}>
              Are you sure you want to revoke attendance?
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#7c2d12' }}>
              {revokeConfirmDialog.userName || 'Unknown'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
          <Button onClick={closeRevokeConfirmDialog}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRevokeAttendance} sx={{ fontWeight: 700 }}>
            Revoke Attendance
          </Button>
        </DialogActions>
      </Dialog>

<Dialog
        open={retryMatchDialogOpen}
        onClose={handleRetryMatchDialogClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: {
              xs: 'calc(100% - 24px)',
              sm: retryMatchStackImages.length > 3 ? 700 : retryMatchStackImages.length > 1 ? 560 : 460,
            },
            maxWidth: 'unset',
            m: { xs: 1.5, sm: 2 },
          }
        }}
      >
        <DialogTitle>Retry Match</DialogTitle>
        <DialogContent
          dividers
          ref={retryMatchContentRef}
          sx={{
            maxHeight: { xs: '78vh', sm: '72vh' },
            overflowY: 'auto',
          }}
        >
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Set similarity threshold for this audit tile rematch.
            </Typography>
            {retryMatchStackImages.length > 1 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Select Image ({retryMatchStackImages.length} available)
                </Typography>
                {retryMatchImageLoading && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Loading images...
                  </Typography>
                )}
                <Box
                  onWheel={(e) => {
                    e.preventDefault();
                    e.currentTarget.scrollTop += e.deltaY * 2.2;
                  }}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 190px))',
                    justifyContent: 'start',
                    gap: { xs: 1.5, sm: 2 },
                    maxHeight: { xs: '48vh', sm: '360px' },
                    overflowY: 'auto',
                    p: 0.5,
                  }}
                >
                  {retryMatchStackImages.map((item, idx) => {
                    const isSelected = String(retryMatchSelectedAuditId || '') === String(item.uniqueId || '');
                    const capturedTs = getCapturedTimestamp(item.log);
                    return (
                      <Card
                        key={item.uniqueId || item.log?.audit_id || idx}
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          border: isSelected ? `3px solid ${PRIMARY}` : '1px solid',
                          borderColor: isSelected ? PRIMARY : 'divider',
                          '&:hover': {
                            boxShadow: 3,
                            borderColor: PRIMARY,
                          },
                          transition: 'all 0.2s',
                        }}
                        onClick={() => {
                          setRetryMatchSelectedAuditId(item.uniqueId);
                          setRetryMatchAudit(item.log);
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '96%',
                            bgcolor: '#f5f5f5',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            component="img"
                            src={item.image || ANONYMOUS_IMAGE}
                            alt="Retry match candidate"
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {isSelected && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: PRIMARY,
                                color: '#fff',
                                borderRadius: '50%',
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: PRIMARY,
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          {item.isMain && (
                            <Chip
                              label="Main"
                              size="small"
                              sx={{
                                position: 'absolute',
                                bottom: 4,
                                left: 4,
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                color: '#fff',
                                fontSize: '0.65rem',
                                height: 18,
                              }}
                            />
                          )}
                        </Box>
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="caption" noWrap sx={{ fontSize: '0.72rem' }}>
                            {capturedTs?.iso ? formatDateWithSeconds(capturedTs.iso) : '-'}
                          </Typography>
                          {item.log?.audit_id && (
                            <Typography variant="caption" display="block" noWrap sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                              ID: {item.log.audit_id}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: { xs: 'block', sm: 'flex' }, alignItems: 'flex-start', gap: { xs: 0.75, sm: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 'fit-content',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    lineHeight: { xs: 'normal', sm: '40px' },
                  }}
                >
                  Similarity Threshold: {parseFloat(retryMatchThreshold || 0).toFixed(2)}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0, maxWidth: { xs: 320, sm: 430 }, mt: { xs: 0.25, sm: 0.5 } }}>
                  <Slider
                    key={`retry-threshold-${retryMatchDialogOpen ? 'open' : 'closed'}-${retryMatchSelectedAuditId || 'none'}-${retryMatchThreshold}`}
                    defaultValue={parseFloat(retryMatchThreshold || 0.6)}
                    onChange={(e, newValue) => {
                      retryMatchThresholdRef.current = Number(newValue);
                    }}
                    onChangeCommitted={(e, newValue) => {
                      const v = Number(newValue);
                      retryMatchThresholdRef.current = v;
                      setRetryMatchThreshold(String(v));
                    }}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => Number(v).toFixed(2)}
                    min={0}
                    max={1}
                    step={0.01}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 0.25, label: '0.25' },
                      { value: 0.5, label: '0.5' },
                      { value: 0.75, label: '0.75' },
                      { value: 1, label: '1' },
                    ]}
                    sx={{
                      width: '100%',
                      '& .MuiSlider-track': {
                        height: 6,
                        marginTop: '-4px',
                      },
                      '& .MuiSlider-rail': {
                        height: 6,
                        marginTop: '-4px',
                      },
                      '& .MuiSlider-thumb': {
                        width: 14,
                        height: 14,
                        marginTop: '-5px',
                        border: '2px solid white', // optional - better look ke liye
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      },
                       '& .MuiSlider-markLabel': {
                         fontSize: '0.65rem',
                         top: 20,
                       },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider />

            <Box
              ref={retryMatchResultRef}
              sx={{
                minHeight: (retryMatchResultLoading || retryMatchResultData)
                  ? { xs: 320, sm: 340 }
                  : 'auto',
              }}
            >
              {retryMatchResultLoading ? (
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Matched Result</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                      gap: { xs: 2, sm: 3 },
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">Audit Capture</Typography>
                      <Skeleton variant="rounded" sx={{ mt: 0.5, width: '100%', aspectRatio: '1 / 1', borderRadius: 1 }} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        fontSize: { xs: '1.4rem', sm: '1.8rem' },
                        fontWeight: 700,
                        transform: { xs: 'rotate(90deg)', sm: 'none' },
                      }}
                    >
                      →
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Matched Employee Profile</Typography>
                      <Skeleton variant="rounded" sx={{ mt: 0.5, width: '100%', aspectRatio: '1 / 1', borderRadius: 1 }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    <Skeleton variant="text" width="88%" />
                    <Skeleton variant="text" width="72%" />
                    <Skeleton variant="text" width="64%" />
                    <Skeleton variant="text" width="70%" />
                  </Box>
                  <Stack spacing={1.5} sx={{ py: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={26} />
                    <Typography variant="body2" color="text.secondary">Preparing match result...</Typography>
                  </Stack>
                </Stack>
              ) : retryMatchResultData ? (
                (() => {
                  const rawScore = Number(retryMatchResultData?.result?.confidence_score);
                  const rawThreshold = Number(retryMatchResultData?.threshold);
                  const score = Number.isFinite(rawScore) ? Math.max(0, Math.min(1, rawScore)) : 0;
                  const threshold = Number.isFinite(rawThreshold) ? Math.max(0, Math.min(1, rawThreshold)) : 0;
                  return (
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Matched Result</Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                          gap: { xs: 2, sm: 3 },
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">Audit Capture</Typography>
                          <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden', bgcolor: '#f5f5f5', aspectRatio: '1 / 1' }}>
                            <Box
                              component="img"
                              src={retryMatchResultData?.auditImage || ANONYMOUS_IMAGE}
                              alt="Audit capture"
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                            fontSize: { xs: '1.4rem', sm: '1.8rem' },
                            fontWeight: 700,
                            transform: { xs: 'rotate(90deg)', sm: 'none' },
                          }}
                        >
                          →
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Matched Employee Profile</Typography>
                          <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden', bgcolor: '#f5f5f5', aspectRatio: '1 / 1' }}>
                            <Box
                              component="img"
                              src={retryMatchResultData?.employeeImage || ANONYMOUS_IMAGE}
                              alt="Employee profile"
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                        <Typography variant="body2"><strong>Name:</strong> {retryMatchResultData?.result?.recognized_name || 'Unknown'}</Typography>
                        <Typography variant="body2"><strong>Person ID:</strong> {retryMatchResultData?.result?.recognized_person_id || '-'}</Typography>
                        <Typography variant="body2"><strong>Score:</strong> {score.toFixed(2)}</Typography>
                        <Typography variant="body2"><strong>Threshold:</strong> {threshold.toFixed(2)}</Typography>
                      </Box>
                    </Stack>
                  );
                })()
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
                  Match result will appear here after clicking Match.
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={handleRetryMatchDialogClose}
            disabled={retryMatchAttendanceSubmitting || retryMatchResultLoading}
          >
            Close
          </Button>
          <Button
            onClick={handleRetryMatchContinue}
            disabled={retryMatchAttendanceSubmitting || retryMatchResultLoading}
          >
            {retryMatchResultData ? 'Rematch' : 'Match'}
          </Button>
          <Button
            variant="contained"
            onClick={handleRetryMatchApply}
            disabled={!retryMatchResultData || retryMatchAttendanceSubmitting || retryMatchResultLoading}
          >
            {retryMatchAttendanceSubmitting ? 'Updating…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const AuditPresentView = (props) => {
  if (typeof props?.setAPersonName === 'function') {
    return <AuditPresentContent {...props} />;
  }
  return <AuditPresentStandalone />;
};

export default AuditPresentView;
